<template>
  <Transition name="consultant-creation-overlay">
    <div
      v-if="visible"
      class="consultant-creation-dialog-modal-overlay consultant-creation-dialog-modal-overlay--glass"
      @click.self="handleOverlayClick"
    >
      <div
        class="consultant-creation-dialog-modal-card consultant-creation-dialog-modal-card--glass consultant-creation-dialog-modal-card--enter"
        role="dialog"
        aria-labelledby="consultant-creation-dialog-title"
        title="Drag the bottom-right corner to resize the dialog"
        @click.stop
      >
      <div class="consultant-creation-dialog-modal-header consultant-creation-dialog-modal-header--glass">
        <h2 id="consultant-creation-dialog-title" class="consultant-creation-dialog-modal-title">
          Create New Consultant
        </h2>
        <button
          type="button"
          class="consultant-creation-dialog-modal-close-btn"
          aria-label="Close"
          title="Close"
          @click="handleClose"
        >
          <i class="bi bi-x-lg" aria-hidden="true"></i>
        </button>
      </div>

      <div class="consultant-creation-dialog-modal-body consultant-creation-dialog-modal-body--glass">
        <form class="consultant-creation-dialog-form">
      <div v-if="!isFulltimeCompanyType && validationErrors.serviceSelection" class="consultant-creation-dialog-service-error">
        {{ validationErrors.serviceSelection }}
        </div>
      
      <TabNavigation
        v-model="activeTab"
        :tabs="visibleTabs"
        @update:modelValue="handleTabChange"
      />

      <!-- Tab Content -->
      <div class="consultant-creation-dialog-tab-content consultant-creation-dialog-tab-content--glass">
        <!-- Basic Information Tab -->
        <div
          class="consultant-creation-dialog-tab-panel consultant-creation-dialog-tab-panel--glass"
          :class="{
            'consultant-creation-dialog-tab-panel--active': activeTab === 'basic',
            'consultant-creation-dialog-tab-panel--enter': tabPanelEnter === 'basic',
          }"
          :aria-hidden="activeTab !== 'basic'"
          @animationend="onTabPanelAnimEnd($event, 'basic')"
        >
          <div class="consultant-creation-dialog-form-col consultant-creation-dialog-stunning-form-col consultant-creation-dialog-form-section--glass">
          <!-- Row 1: Consultant ID, Consultant Name, Email, Phone -->
          <div class="consultant-creation-dialog-form-group-row">
            <div class="consultant-creation-dialog-form-group consultant-creation-dialog-form-group--label-input">
              <LabelInputField
                input-id="ccd-consultant-id"
                label="Consultant ID"
                v-model="form.consultantId"
                placeholder="Consultant ID"
                required
                flex
                :has-error="consultantIdExists || !!validationErrors.consultantId"
                @input="handleConsultantIdInput"
              >
                <div v-if="checkingConsultantId" class="consultant-creation-dialog-checking-text">Checking availability...</div>
                <div v-if="consultantIdExists" class="consultant-creation-dialog-error-text">This Consultant ID already exists in this company.</div>
                <div v-if="validationErrors.consultantId" class="consultant-creation-dialog-error-text">{{ validationErrors.consultantId }}</div>
              </LabelInputField>
            </div>
            <div class="consultant-creation-dialog-form-group consultant-creation-dialog-form-group--label-input">
              <LabelInputField
                input-id="ccd-consultant-name"
                label="Consultant Name"
                v-model="form.consultantName"
                placeholder="Consultant Name"
                required
                flex
                :has-error="!!validationErrors.consultantName"
                @input="clearFieldError('consultantName')"
              >
                <div v-if="validationErrors.consultantName" class="consultant-creation-dialog-error-text">{{ validationErrors.consultantName }}</div>
              </LabelInputField>
            </div>
            <div class="consultant-creation-dialog-form-group consultant-creation-dialog-form-group--label-input">
              <LabelInputField
                input-id="ccd-consultant-email"
                label="Email"
                v-model="form.email"
                type="email"
                placeholder="Email"
                required
                flex
                :has-error="!!validationErrors.email"
                @input="clearFieldError('email')"
                @blur="validateEmail"
              >
                <div v-if="validationErrors.email" class="consultant-creation-dialog-error-text">{{ validationErrors.email }}</div>
              </LabelInputField>
            </div>
            <div class="consultant-creation-dialog-form-group consultant-creation-dialog-form-group--label-input">
              <LabelInputField
                input-id="ccd-consultant-phone"
                label="Phone"
                v-model="form.phone"
                placeholder="Phone"
                required
                flex
                :has-error="!!validationErrors.phone"
                @input="clearFieldError('phone')"
              >
                <div v-if="validationErrors.phone" class="consultant-creation-dialog-error-text">{{ validationErrors.phone }}</div>
              </LabelInputField>
            </div>
          </div>
          <!-- Row 2: Consultant Type + Pay Rate / Fixed Salary for Fulltime - Company -->
          <div class="consultant-creation-dialog-form-group-row">
            <div class="consultant-creation-dialog-form-group consultant-creation-dialog-form-group--label-dropdown">
              <LabelDropdown
                label="Consultant Type"
                select-id="ccd-consultant-type"
                v-model="consultantTypeSelection"
                flex
                wide
                required
                :disabled="lockConsultantType"
                menu-class="consultant-creation-dialog-dropdown-menu"
                :options="consultantTypeDropdownOptions"
                :has-error="!!validationErrors.consultantType"
                @change="handleConsultantTypeChange"
              >
                <div v-if="validationErrors.consultantType" class="consultant-creation-dialog-error-text">{{ validationErrors.consultantType }}</div>
              </LabelDropdown>
            </div>
            <div
              v-if="isFulltimeCompanyType && !form.isFixedSalary"
              key="ccd-basic-company-pay-rate"
              class="consultant-creation-dialog-form-group consultant-creation-dialog-form-group--label-input"
            >
              <LabelInputField
                input-id="ccd-basic-pay-rate"
                label="Pay Rate"
                v-model="form.payRate"
                placeholder="0.00"
                flex
                :has-error="!!validationErrors.payRate"
                @input="handlePriceInput('payRate')"
                @blur="formatNumericInput('payRate')"
              >
                <div class="consultant-creation-dialog-helper-text">
                  <i class="bi bi-info-circle"></i>
                  Hourly or payroll pay rate for company fulltime staff.
                </div>
                <div v-if="validationErrors.payRate" class="consultant-creation-dialog-error-text">{{ validationErrors.payRate }}</div>
              </LabelInputField>
            </div>
            <div
              v-else-if="isFulltimeCompanyType"
              key="ccd-basic-company-fixed-salary"
              class="consultant-creation-dialog-form-group consultant-creation-dialog-form-group--label-input"
            >
              <LabelInputField
                input-id="ccd-basic-fixed-salary"
                label="Fixed Salary (USD)"
                v-model="form.fixedSalary"
                placeholder="0.00"
                flex
                :has-error="!!validationErrors.fixedSalary"
                @input="handlePriceInput('fixedSalary')"
                @blur="formatNumericInput('fixedSalary')"
              >
                <div v-if="validationErrors.fixedSalary" class="consultant-creation-dialog-error-text">{{ validationErrors.fixedSalary }}</div>
              </LabelInputField>
            </div>
          </div>
          <!-- Fulltime - Company: Fixed Salary toggle + Active -->
          <div
            v-if="isFulltimeCompanyType"
            class="consultant-creation-dialog-form-group-row"
          >
            <div class="consultant-creation-dialog-form-group consultant-creation-dialog-form-group--label-checkbox">
              <LabelCheckboxField
                input-id="ccd-basic-is-fixed-salary"
                label="Fixed Salary"
                v-model="form.isFixedSalary"
                flex
                @update:modelValue="onIsFixedSalaryChange"
              />
            </div>
            <div class="consultant-creation-dialog-form-group consultant-creation-dialog-form-group--label-checkbox">
              <LabelCheckboxField
                input-id="ccd-active-fulltime-company"
                label="Active"
                v-model="form.active"
                flex
              />
            </div>
          </div>
          <div
            v-if="!isFulltimeCompanyType"
            class="consultant-creation-dialog-form-group-row"
          >
            <div class="consultant-creation-dialog-form-group consultant-creation-dialog-form-group--label-dropdown">
              <LabelDropdown
                label="Customer"
                select-id="ccd-customer"
                v-model="form.customerId"
                flex
                wide
                searchable
                menu-class="consultant-creation-dialog-dropdown-menu"
                :options="customerDropdownOptions"
                @change="handleCustomerChange"
              />
            </div>
            <div class="consultant-creation-dialog-form-group consultant-creation-dialog-form-group--label-dropdown">
              <LabelDropdown
                label="Invoicing Frequency"
                select-id="ccd-invoicing-frequency"
                v-model="form.invoicingFrequencyId"
                flex
                wide
                searchable
                menu-class="consultant-creation-dialog-dropdown-menu"
                :options="invoicingFrequencyDropdownOptions"
                @change="handleInvoicingFrequencyChange"
              />
              <div v-if="isInvoiceFrequencyAutoSelected" class="consultant-creation-dialog-auto-selected-indicator">
                <i class="bi bi-check-circle-fill"></i>
                <span>Automatically selected from customer</span>
              </div>
            </div>
            <div class="consultant-creation-dialog-form-group consultant-creation-dialog-form-group--label-dropdown">
              <LabelDropdown
                :key="'payment-terms-' + paymentTerms.length"
                label="Payment Terms"
                select-id="ccd-payment-terms"
                v-model="form.paymentTermId"
                flex
                wide
                searchable
                menu-class="consultant-creation-dialog-dropdown-menu"
                :options="paymentTermsDropdownOptions"
                @change="handlePaymentTermChange"
              />
              <div v-if="isPaymentTermsAutoSelected" class="consultant-creation-dialog-auto-selected-indicator">
                <i class="bi bi-check-circle-fill"></i>
                <span>Automatically selected from customer</span>
              </div>
            </div>
          </div>
          <!-- Row 4: Commission Item, Active (non Fulltime - Company) -->
          <div
            v-if="!isFulltimeCompanyType"
            class="consultant-creation-dialog-form-group-row"
          >
            <div class="consultant-creation-dialog-form-group consultant-creation-dialog-form-group--label-checkbox">
              <LabelCheckboxField
                input-id="ccd-commission-item"
                label="Commission item"
                v-model="form.commissionItem"
                flex
              />
            </div>
            <div class="consultant-creation-dialog-form-group consultant-creation-dialog-form-group--label-checkbox">
              <LabelCheckboxField
                input-id="ccd-active"
                label="Active"
                v-model="form.active"
                flex
              />
            </div>
          </div>
          </div>
        </div>

        <!-- Service Information Tab -->
        <div
          class="consultant-creation-dialog-tab-panel consultant-creation-dialog-tab-panel--glass"
          :class="{
            'consultant-creation-dialog-tab-panel--active': activeTab === 'service',
            'consultant-creation-dialog-tab-panel--enter': tabPanelEnter === 'service',
          }"
          :aria-hidden="activeTab !== 'service'"
          @animationend="onTabPanelAnimEnd($event, 'service')"
        >
          <div class="consultant-creation-dialog-form-col consultant-creation-dialog-stunning-form-col consultant-creation-dialog-form-section--glass">
            <!-- Row 1: Service Selection - Only show "I buy this service" for C2C consultants -->
            <div class="consultant-creation-dialog-form-group-row">
              <div class="consultant-creation-dialog-form-group consultant-creation-dialog-form-group--label-checkbox">
                <LabelCheckboxField
                  input-id="ccd-sell-service"
                  label="I sell this service"
                  :model-value="form.sellService"
                  :disabled="isC2CType"
                  flex
                  @update:modelValue="onSellServiceChange"
                />
              </div>
              <div
                v-if="isC2CType"
                class="consultant-creation-dialog-form-group consultant-creation-dialog-form-group--label-checkbox"
              >
                <LabelCheckboxField
                  input-id="ccd-buy-service"
                  label="I buy this service"
                  :model-value="form.buyService"
                  disabled
                  flex
                  @update:modelValue="onBuyServiceChange"
                />
              </div>
              <!-- Show info when buy service is not available for non-C2C types -->
              <div v-if="!isC2CType" class="consultant-creation-dialog-service-info">
                <i class="bi bi-info-circle"></i>
                <span>Buy service option is only available for C2C consultant type</span>
              </div>
            </div>
            
            <!-- C2C Info Message -->
            <div v-if="isC2CType" class="consultant-creation-dialog-c2c-info-message">
              <i class="bi bi-info-circle"></i>
              <span>For C2C consultants, both "I sell this service" and "I buy this service" are automatically selected and required.</span>
            </div>
            
            <!-- Service Configuration Section - Only show if at least one service is selected -->
            <div v-if="form.sellService || form.buyService" class="consultant-creation-dialog-service-config">
              <!-- Left Column: Sell Service Fields - Only show if sell service is checked -->
              <div v-if="form.sellService" class="consultant-creation-dialog-service-column">
                <h4 class="consultant-creation-dialog-service-header">Sell Service Configuration</h4>
                <div class="consultant-creation-dialog-form-group-row">
                  <div class="consultant-creation-dialog-form-group consultant-creation-dialog-form-group--label-input">
                    <LabelInputField
                      input-id="ccd-sales-price"
                      label="Sales Price (USD)"
                      v-model="form.salesPrice"
                      placeholder="0.00"
                      :required="form.sellService"
                      :disabled="!form.sellService"
                      flex
                      :has-error="!!validationErrors.salesPrice"
                      @input="handlePriceInput('salesPrice')"
                      @blur="formatNumericInput('salesPrice')"
                    >
                      <div v-if="validationErrors.salesPrice" class="consultant-creation-dialog-error-text">{{ validationErrors.salesPrice }}</div>
                    </LabelInputField>
                  </div>
                  <div class="consultant-creation-dialog-form-group consultant-creation-dialog-form-group--label-dropdown">
                    <LabelDropdown
                      label="Income Account"
                      select-id="ccd-income-account"
                      v-model="form.incomeAccountId"
                      flex
                      wide
                      searchable
                      :required="form.sellService"
                      :disabled="!form.sellService"
                      menu-class="consultant-creation-dialog-dropdown-menu"
                :options="incomeAccountDropdownOptions"
                      :has-error="!!validationErrors.incomeAccountId"
                      @change="handleIncomeAccountChange"
                    >
                      <div v-if="validationErrors.incomeAccountId" class="consultant-creation-dialog-error-text">{{ validationErrors.incomeAccountId }}</div>
                    </LabelDropdown>
                  </div>
                </div>
                <div class="consultant-creation-dialog-form-group-row">
                  <div class="consultant-creation-dialog-form-group consultant-creation-dialog-form-group--label-input">
                    <LabelInputField
                      input-id="ccd-standard-cost"
                      label="Standard Cost (USD)"
                      v-model="form.standardCost"
                      placeholder="0.00"
                      required
                      flex
                      :has-error="!!validationErrors.standardCost"
                      @input="handlePriceInput('standardCost')"
                      @blur="formatNumericInput('standardCost')"
                    >
                      <div v-if="form.salesPrice && form.standardCost === form.salesPrice" class="consultant-creation-dialog-helper-text">
                        <i class="bi bi-info-circle"></i>
                        Initially set to match Sales Price. You can edit this value.
                      </div>
                      <div v-if="validationErrors.standardCost" class="consultant-creation-dialog-error-text">{{ validationErrors.standardCost }}</div>
                    </LabelInputField>
                  </div>
                  <div v-if="showServicePayRate" class="consultant-creation-dialog-form-group consultant-creation-dialog-form-group--label-checkbox consultant-creation-dialog-form-group--fixed-salary-toggle">
                    <LabelCheckboxField
                      input-id="ccd-is-fixed-salary"
                      label="Fixed Salary"
                      v-model="form.isFixedSalary"
                      flex
                      @update:modelValue="onIsFixedSalaryChange"
                    />
                  </div>
                </div>
                <div v-if="showServicePayRate" class="consultant-creation-dialog-form-group-row">
                  <div v-if="!form.isFixedSalary" class="consultant-creation-dialog-form-group consultant-creation-dialog-form-group--label-input">
                    <LabelInputField
                      input-id="ccd-pay-rate"
                      label="Pay Rate"
                      v-model="form.payRate"
                      placeholder="0.00 or 80%"
                      flex
                      :has-error="!!validationErrors.payRate"
                      @input="handlePriceInput('payRate')"
                      @blur="formatNumericInput('payRate')"
                    >
                      <div class="consultant-creation-dialog-helper-text">
                        <i class="bi bi-info-circle"></i>
                        {{ payRateInfoText }}
                      </div>
                      <div v-if="validationErrors.payRate" class="consultant-creation-dialog-error-text">{{ validationErrors.payRate }}</div>
                    </LabelInputField>
                  </div>
                  <div v-else class="consultant-creation-dialog-form-group consultant-creation-dialog-form-group--label-input">
                    <LabelInputField
                      input-id="ccd-fixed-salary"
                      label="Fixed Salary (USD)"
                      v-model="form.fixedSalary"
                      placeholder="0.00"
                      flex
                      :has-error="!!validationErrors.fixedSalary"
                      @input="handlePriceInput('fixedSalary')"
                      @blur="formatNumericInput('fixedSalary')"
                    >
                      <div v-if="validationErrors.fixedSalary" class="consultant-creation-dialog-error-text">{{ validationErrors.fixedSalary }}</div>
                    </LabelInputField>
                  </div>
                </div>
                <div class="consultant-creation-dialog-form-group consultant-creation-dialog-form-group--label-description">
                  <LabelDescriptionField
                    input-id="ccd-sales-description"
                    label="Description on Sales Transaction"
                    v-model="form.salesDescription"
                    :rows="2"
                    :disabled="!form.sellService"
                    flex
                  />
                </div>
            </div>
              
              <!-- Right Column: Buy Service Fields - Only show if buy service is checked -->
              <div v-if="form.buyService" class="consultant-creation-dialog-service-column">
                <h4 class="consultant-creation-dialog-service-header">Buy Service Configuration</h4>
                <div class="consultant-creation-dialog-form-group-row">
                  <div class="consultant-creation-dialog-form-group consultant-creation-dialog-form-group--label-input">
                    <LabelInputField
                      input-id="ccd-purchase-price"
                      label="Purchase Price (USD)"
                      v-model="form.purchasePrice"
                      placeholder="0.00"
                      :required="form.buyService"
                      :disabled="!form.buyService"
                      flex
                      :has-error="!!validationErrors.purchasePrice"
                      @input="handlePriceInput('purchasePrice')"
                      @blur="formatNumericInput('purchasePrice')"
                    >
                      <div v-if="validationErrors.purchasePrice" class="consultant-creation-dialog-error-text">{{ validationErrors.purchasePrice }}</div>
                    </LabelInputField>
                  </div>
                  <div class="consultant-creation-dialog-form-group consultant-creation-dialog-form-group--label-dropdown">
                    <LabelDropdown
                      label="Expense Account"
                      select-id="ccd-expense-account"
                      v-model="form.expenseAccountId"
                      flex
                      wide
                      searchable
                      :required="form.buyService"
                      :disabled="!form.buyService"
                      menu-class="consultant-creation-dialog-dropdown-menu"
                :options="expenseAccountDropdownOptions"
                      :has-error="!!validationErrors.expenseAccountId"
                      @change="handleExpenseAccountChange"
                    >
                      <div v-if="validationErrors.expenseAccountId" class="consultant-creation-dialog-error-text">{{ validationErrors.expenseAccountId }}</div>
                    </LabelDropdown>
                  </div>
                </div>
                <div class="consultant-creation-dialog-form-group-row">
                  <div class="consultant-creation-dialog-form-group consultant-creation-dialog-form-group--label-dropdown">
                    <LabelDropdown
                      label="Preferred Vendor"
                      select-id="ccd-preferred-vendor"
                      v-model="form.preferredVendorId"
                      flex
                      wide
                      searchable
                      :required="form.buyService"
                      :disabled="!form.buyService || lockVendor"
                      menu-class="consultant-creation-dialog-dropdown-menu"
                :options="preferredVendorDropdownOptions"
                      :has-error="!!validationErrors.preferredVendorId"
                      @change="handlePreferredVendorChange"
                    >
                      <div v-if="validationErrors.preferredVendorId" class="consultant-creation-dialog-error-text">{{ validationErrors.preferredVendorId }}</div>
                    </LabelDropdown>
                  </div>
                  <div class="consultant-creation-dialog-form-group consultant-creation-dialog-form-group--label-input">
                    <LabelInputField
                      input-id="ccd-vendor-service-no"
                      label="Vendor Service No"
                      v-model="form.vendorServiceNo"
                      placeholder="Vendor Service No"
                      :disabled="!form.buyService"
                      flex
                    />
                  </div>
                </div>
                <div class="consultant-creation-dialog-form-group consultant-creation-dialog-form-group--label-description">
                  <LabelDescriptionField
                    input-id="ccd-purchase-description"
                    label="Description on Purchase Transaction"
                    v-model="form.purchaseDescription"
                    :rows="2"
                    :disabled="!form.buyService"
                    flex
                  />
                </div>
            </div>
            </div>
            
          </div>
        </div>

        <!-- Address Information Tab -->
        <div
          class="consultant-creation-dialog-tab-panel consultant-creation-dialog-tab-panel--glass"
          :class="{
            'consultant-creation-dialog-tab-panel--active': activeTab === 'address',
            'consultant-creation-dialog-tab-panel--enter': tabPanelEnter === 'address',
          }"
          :aria-hidden="activeTab !== 'address'"
          @animationend="onTabPanelAnimEnd($event, 'address')"
        >
        <div class="consultant-creation-dialog-contacts-section consultant-creation-dialog-form-section--glass">
          <div class="consultant-creation-dialog-addresses-header">
          <h3>Addresses</h3>
            <div class="consultant-creation-dialog-add-address-container">
              <PrimaryButton
                label="Add Address"
                icon="bi bi-plus-circle"
                compact
                :disabled="!canAddMoreAddresses"
                @click="addNewAddressRow"
              />
              
              <!-- Tooltip for when no addresses exist -->
              <div v-if="addresses.length === 0 && newAddresses.length === 0" class="consultant-creation-dialog-add-address-tooltip">
                <div class="consultant-creation-dialog-tooltip-content">
                  <div class="consultant-creation-dialog-tooltip-icon">
                    <i class="bi bi-geo-alt" aria-hidden="true"></i>
                  </div>
                  <p>No addresses added yet</p>
                  <p class="consultant-creation-dialog-tooltip-subtitle">Two address fields are provided by default (Maximum 2 addresses)</p>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Address Cards -->
          <div class="consultant-creation-dialog-addresses-grid">
            <!-- Existing Addresses -->
            <div v-for="(address, idx) in addresses" :key="'address-' + idx" class="consultant-creation-dialog-address-card">
              <div class="consultant-creation-dialog-address-card-header consultant-creation-dialog-form-group--label-dropdown">
                <LabelDropdown
                  label="Address Type"
                  :select-id="`ccd-address-type-${idx}`"
                  v-model="address.type"
                  flex
                  wide
                  required
                  :disabled="editingAddressIdx !== idx"
                  menu-class="consultant-creation-dialog-dropdown-menu"
                :options="addressTypeDropdownOptions(idx, false)"
                />
              </div>
              
              <div class="consultant-creation-dialog-address-fields">
                <div class="consultant-creation-dialog-address-field-row">
                  <div class="consultant-creation-dialog-address-field consultant-creation-dialog-form-group--label-input">
                    <LabelInputField
                      :input-id="`ccd-address-line1-${idx}`"
                      label="Address Line 1"
                      v-model="address.address1"
                      placeholder="Street address"
                      flex
                      :readonly="editingAddressIdx !== idx"
                    />
                  </div>
                  <div class="consultant-creation-dialog-address-field consultant-creation-dialog-form-group--label-input">
                    <LabelInputField
                      :input-id="`ccd-address-line2-${idx}`"
                      label="Address Line 2"
                      v-model="address.address2"
                      placeholder="Apartment, suite, etc."
                      flex
                      :readonly="editingAddressIdx !== idx"
                    />
                  </div>
                </div>
                
                <AddressSelector
                  :country="address.country"
                  :state="address.state"
                  :city="address.city"
                  :postal-code="address.postalCode"
                  :disabled="editingAddressIdx !== idx"
                  @update:country="address.country = $event"
                  @update:state="address.state = $event"
                  @update:city="address.city = $event"
                  @update:postalCode="address.postalCode = $event"
                />
              </div>
              
              <!-- Edit and Delete buttons for existing addresses (when not editing) -->
              <div v-if="editingAddressIdx !== idx" class="consultant-creation-dialog-address-save-actions">
                <SecondaryButton label="Edit" compact @click.prevent="editAddress(idx)" />
                <PrimaryButton label="Delete" icon="bi bi-trash" compact @click.prevent="deleteAddress(idx)" />
              </div>
              
              <!-- Save and Cancel buttons for editing existing addresses -->
              <div v-if="editingAddressIdx === idx" class="consultant-creation-dialog-address-save-actions">
                <SecondaryButton label="Cancel" compact @click.prevent="cancelEditAddress" />
                <PrimaryButton label="Update" icon="bi bi-check-lg" compact @click.prevent="updateAddress(idx)" />
              </div>
            </div>

            <!-- New Address Cards -->
            <div v-for="(na, nidx) in newAddresses" :key="'new-address-' + nidx" class="consultant-creation-dialog-address-card consultant-creation-dialog-address-card-new">
              <div class="consultant-creation-dialog-address-card-header consultant-creation-dialog-form-group--label-dropdown">
                <LabelDropdown
                  label="Address Type"
                  :select-id="`ccd-new-address-type-${nidx}`"
                  v-model="na.type"
                  flex
                  wide
                  required
                  menu-class="consultant-creation-dialog-dropdown-menu"
                :options="addressTypeDropdownOptions(nidx, true)"
                />
              </div>
              
              <div class="consultant-creation-dialog-address-fields">
                <div class="consultant-creation-dialog-address-field-row">
                  <div class="consultant-creation-dialog-address-field consultant-creation-dialog-form-group--label-input">
                    <LabelInputField
                      :input-id="`ccd-new-address-line1-${nidx}`"
                      label="Address Line 1"
                      v-model="na.address1"
                      placeholder="Street address"
                      flex
                    />
                  </div>
                  <div class="consultant-creation-dialog-address-field consultant-creation-dialog-form-group--label-input">
                    <LabelInputField
                      :input-id="`ccd-new-address-line2-${nidx}`"
                      label="Address Line 2"
                      v-model="na.address2"
                      placeholder="Apartment, suite, etc."
                      flex
                    />
                  </div>
                </div>
                
                <AddressSelector
                  :country="na.country"
                  :state="na.state"
                  :city="na.city"
                  :postal-code="na.postalCode"
                  @update:country="na.country = $event"
                  @update:state="na.state = $event"
                  @update:city="na.city = $event"
                  @update:postalCode="na.postalCode = $event"
                />
              </div>
              
              <!-- Save and Cancel buttons for new addresses -->
              <div class="consultant-creation-dialog-address-save-actions">
                <SecondaryButton label="Cancel" compact @click.prevent="cancelNewAddress(nidx)" />
                <PrimaryButton label="Save" icon="bi bi-check-lg" compact @click.prevent="saveNewAddress(nidx)" />
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
        <!-- Custom Action Buttons -->
        <div class="consultant-creation-dialog-form-actions consultant-creation-dialog-form-actions--glass">
          <div class="consultant-creation-dialog-action-buttons">
            <SecondaryButton label="Cancel" icon="bi bi-x-lg" @click="cancel" />
            <PrimaryButton
              v-if="!isLastVisibleTab"
              label="Next"
              icon="bi bi-arrow-right"
              @click="handleNext"
            />
            <PrimaryButton
              v-if="isLastVisibleTab"
              label="Save and Close"
              icon="bi bi-check-lg"
              @click="saveAndClose"
            />
          </div>
        </div>
    </form>
      </div>
      </div>
    </div>
  </Transition>

    <SuccessModal 
      :show="showSuccessModal"
      :title="successTitle"
      :message="successMessage"
      @close="closeSuccessModal"
    />
    <ErrorModal 
      :show="showErrorModal" 
      :title="'Error'"
      :message="errorMessage"
      @close="closeErrorModal" 
    />
     <AddNewPaymentTerm
       :visible="showAddPaymentTermModal"
       :companyId="$route.params.id"
       @ok="onAddPaymentTermOk"
       @cancel="onAddPaymentTermCancel"
     />
     <AddNewInvoiceFrequency
       :visible="showAddInvoiceFrequencyModal"
       :companyId="$route.params.id"
       @ok="onAddInvoiceFrequencyOk"
       @cancel="onAddInvoiceFrequencyCancel"
     />
     <SuccessModal 
       :show="showInvoiceFrequencySuccessModal"
       title="Success"
       message="Invoice frequency created successfully!"
       buttonText="OK"
       @close="closeInvoiceFrequencySuccessModal"
     />
     <CustomerCreationDialog
       :visible="showAddCustomerModal"
       :companyId="$route.params.id"
       @ok="onAddCustomerOk"
       @cancel="onAddCustomerCancel"
       @close="onAddCustomerCancel"
     />
     <NewAccountCreationDialog
       :visible="showAddAccountModal"
       :companyId="$route.params.id"
       @ok="onAddAccountOk"
       @cancel="onAddAccountCancel"
     />
     <VendorCreationDialog
       :visible="showAddVendorModal"
       :companyId="$route.params.id"
       @ok="onAddVendorOk"
       @cancel="onAddVendorCancel"
       @close="onAddVendorCancel"
     />
     
     <!-- Profit/Loss Information Modal -->
     <ProfitLossModal
       :visible="showProfitLossModal"
       :profit-loss-data="profitLossInfo"
       title="Profit/Loss Analysis"
       :auto-close="true"
       :auto-close-delay="10"
       :close-on-overlay-click="true"
       @close="closeProfitLossModal"
     />
</template>

<script>
import '../styles/ConsultantCreationDialog.css';
import ApiService from '@/services/api.service';
import logger from '@/utils/logger';
import CompanyService from '@/services/company.service';
import customerService from '@/services/customer.service.js';
import AddNewPaymentTerm from '@/modules/company/views/AddNewPaymentTerm.vue';
import AddNewInvoiceFrequency from '@/modules/company/views/AddNewInvoiceFrequency.vue';
import CustomerCreationDialog from '@/modules/customer/views/CustomerCreationDialog.vue';
import NewAccountCreationDialog from '@/modules/company/views/NewAccountCreationDialog.vue';
import VendorCreationDialog from '@/modules/vendor/views/VendorCreationDialog.vue';
import ErrorModal from '@/components/ErrorModal.vue';
import SuccessModal from '@/components/SuccessModal.vue';
import TabNavigation from '@/components/TabNavigation.vue';
import ProfitLossModal from '@/components/ProfitLossModal.vue';
import AddressSelector from '@/components/AddressSelector.vue';
import LabelInputFieldComponent from '@/modules/consultant/components/LabelInputField.vue';
import LabelCheckboxFieldComponent from '@/modules/consultant/components/LabelCheckboxField.vue';
import LabelDescriptionFieldComponent from '@/modules/consultant/components/LabelDescriptionField.vue';
import LabelDropdownComponent from '@/modules/consultant/components/LabelDropdown.vue';
import { PAY_RATE_INFO_TEXT, resolvePayRateFromInput, isAllowedPayRateTyping } from '@/modules/customer/utils/consultantPayRateInput';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';

export default {
  name: 'ConsultantCreationDialog',
  components: {
    AddNewPaymentTerm,
    AddNewInvoiceFrequency,
    CustomerCreationDialog,
    NewAccountCreationDialog,
    VendorCreationDialog,
    ErrorModal,
    SuccessModal,
    TabNavigation,
    ProfitLossModal,
    AddressSelector,
    LabelInputField: LabelInputFieldComponent,
    LabelCheckboxField: LabelCheckboxFieldComponent,
    LabelDescriptionField: LabelDescriptionFieldComponent,
    LabelDropdown: LabelDropdownComponent,
    PrimaryButton,
    SecondaryButton,
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    companyId: {
      type: [String, Number],
      required: false
    },
    prefilledCustomerId: {
      type: [String, Number],
      required: false,
      default: null
    },
    forcedConsultantType: {
      type: String,
      default: null
    },
    lockConsultantType: {
      type: Boolean,
      default: false
    },
    prefilledVendorId: {
      type: [String, Number],
      default: null
    },
    lockVendor: {
      type: Boolean,
      default: false
    },
    closeOnOverlayClick: {
      type: Boolean,
      default: false
    }
  },
  emits: ['ok', 'cancel', 'close'],
  data() {
    return {
      tabs: [
        { id: 'basic', name: 'Basic Information' },
        { id: 'service', name: 'Service Information' },
        { id: 'address', name: 'Address Information' }
      ],
      form: {
        consultantId: '',
        consultantName: '',
        consultantType: 'Fulltime',
        isFulltimeCompany: false,
        email: '',
        phone: '',
        sellService: true,
        salesDescription: '',
        salesPrice: '',
        incomeAccountId: '',
        commissionItem: false,
        standardCost: '',
        active: true,
        customerId: '',
        invoicingFrequencyId: '',
        paymentTermId: '',
        buyService: false, // Only enabled for C2C consultants
        purchaseDescription: '',
        purchasePrice: '',
        isFixedSalary: false,
        payRate: '',
        fixedSalary: '',
        expenseAccountId: '',
        preferredVendorId: '',
        vendorServiceNo: '',
      },
      addresses: [],
      newAddresses: [],
      editingAddressIdx: null,
      incomeAccounts: [],
      expenseAccounts: [],
      validationErrors: {},
      vendors: [],
      customers: [],
      addressTypeError: '',
      paymentTerms: [],
      invoiceFrequencies: [],
      showSuccessModal: false,
      successTitle: 'Success',
      successMessage: '',
      showErrorModal: false,
      errorMessage: '',
      isSaveAndNew: false,
      showAddPaymentTermModal: false,
      previousPaymentTerm: '',
      showAddCustomerModal: false,
      previousCustomerId: null,
      showAddAccountModal: false,
      previousIncomeAccountId: null,
      previousExpenseAccountId: null,
      showAddInvoiceFrequencyModal: false,
      previousInvoicingFrequencyId: null,
      showInvoiceFrequencySuccessModal: false,
      showAddVendorModal: false,
      previousPreferredVendorId: null,
      activeTab: 'basic',
      tabPanelEnter: '',
      showProfitLossModal: false,
      isPaymentTermsAutoSelected: false,
      isInvoiceFrequencyAutoSelected: false,
      priceValidationTimeout: null,
      consultantIdExists: false,
      checkingConsultantId: false,
      consultantIdCheckTimeout: null,
      savedConsultant: null,
    };
  },
  computed: {
    effectiveCompanyId() {
      return this.companyId || this.$route.params.id;
    },
    consultantTypeDropdownOptions() {
      return [
        { value: '', label: 'Select Consultant Type' },
        { value: 'Fulltime-Client', label: 'Fulltime - Client' },
        { value: 'Fulltime-Company', label: 'Fulltime - Company' },
        { value: '1099', label: '1099' },
        { value: 'C2C', label: 'C2C' },
      ];
    },
    consultantTypeSelection: {
      get() {
        if (this.form.consultantType === 'Fulltime') {
          return this.form.isFulltimeCompany ? 'Fulltime-Company' : 'Fulltime-Client';
        }
        return this.form.consultantType || '';
      },
      set(value) {
        const selected = String(value || '');
        if (selected === 'Fulltime-Company') {
          this.form.consultantType = 'Fulltime';
          this.form.isFulltimeCompany = true;
        } else if (selected === 'Fulltime-Client') {
          this.form.consultantType = 'Fulltime';
          this.form.isFulltimeCompany = false;
        } else {
          this.form.consultantType = selected;
          this.form.isFulltimeCompany = false;
        }
      },
    },
    customerDropdownOptions() {
      return [
        { value: '', label: 'Select Customer' },
        ...(this.customers || []).map((customer) => ({
          value: String(customer.id),
          label: customer.customerName,
        })),
        { value: '__add_new__', label: 'Add New Customer' },
      ];
    },
    invoicingFrequencyDropdownOptions() {
      return [
        { value: '', label: 'Select Invoicing Frequency' },
        ...(this.invoiceFrequencies || []).map((frequency) => ({
          value: String(frequency.id),
          label: frequency.name,
        })),
        { value: '__add_new__', label: 'Add New Invoicing Frequency' },
      ];
    },
    paymentTermsDropdownOptions() {
      return [
        { value: '', label: 'Select Payment Term' },
        ...(this.paymentTerms || []).map((term) => ({
          value: String(term.id),
          label: `${term.name} (${term.description})`,
        })),
        { value: '__add_new__', label: 'Add New Payment Term' },
      ];
    },
    incomeAccountDropdownOptions() {
      return [
        { value: '', label: 'Select account' },
        ...(this.incomeAccounts || []).map((acc) => ({
          value: String(acc.id),
          label: `${acc.accountNumber} - ${acc.accountName} - ${acc.accountType}`,
        })),
        { value: '__add_new__', label: 'Add New Account' },
      ];
    },
    expenseAccountDropdownOptions() {
      return [
        { value: '', label: 'Select account' },
        ...(this.expenseAccounts || []).map((acc) => ({
          value: String(acc.id),
          label: `${acc.accountNumber} - ${acc.accountName} - ${acc.accountType}`,
        })),
        { value: '__add_new__', label: 'Add New Account' },
      ];
    },
    preferredVendorDropdownOptions() {
      return [
        { value: '', label: 'Select Vendor' },
        ...(this.vendors || []).map((vendor) => ({
          value: String(vendor.id),
          label: vendor.vendorName || vendor.name,
        })),
        { value: '__add_new__', label: 'Add New Vendor' },
      ];
    },
    canAddMoreAddresses() {
      const totalAddresses = this.addresses.length + this.newAddresses.length;
      return totalAddresses < 2;
    },
    isC2CType() {
      return this.form.consultantType === 'C2C';
    },
    isFulltimeCompanyType() {
      return this.form.consultantType === 'Fulltime' && Boolean(this.form.isFulltimeCompany);
    },
    showPayRate() {
      return this.form.consultantType === 'Fulltime' || this.form.consultantType === '1099';
    },
    showServicePayRate() {
      return this.showPayRate && !this.isFulltimeCompanyType;
    },
    visibleTabs() {
      const tabs = [
        { id: 'basic', name: 'Basic Information' },
      ];
      if (!this.isFulltimeCompanyType) {
        tabs.push({ id: 'service', name: 'Service Information' });
      }
      tabs.push({ id: 'address', name: 'Address Information' });
      return tabs;
    },
    isLastVisibleTab() {
      const tabs = this.visibleTabs.map((t) => t.id);
      return tabs.length > 0 && tabs[tabs.length - 1] === this.activeTab;
    },
    payRateInfoText() {
      return PAY_RATE_INFO_TEXT;
    },
    profitLossInfo() {
      // Show calculation when both sell and buy services are enabled and both prices have values
      if (this.form.sellService && this.form.buyService && 
          this.form.salesPrice && this.form.salesPrice !== '' && 
          this.form.purchasePrice && this.form.purchasePrice !== '') {
        
        const salesPrice = Number(this.form.salesPrice);
        const purchasePrice = Number(this.form.purchasePrice);
        
        // Only show calculation if both values are valid numbers
        if (!isNaN(salesPrice) && !isNaN(purchasePrice) && salesPrice > 0 && purchasePrice > 0) {
          const profit = salesPrice - purchasePrice;
          const percentage = purchasePrice > 0 ? (profit / purchasePrice) * 100 : 0;
          
          if (profit > 0) {
            return {
              type: 'profit',
              amount: profit.toFixed(2),
              percentage: percentage.toFixed(2),
              message: `Profit: $${profit.toFixed(2)} (${percentage.toFixed(2)}%)`
            };
          } else if (profit < 0) {
            return {
              type: 'loss',
              amount: Math.abs(profit).toFixed(2),
              percentage: Math.abs(percentage).toFixed(2),
              message: `Loss: $${Math.abs(profit).toFixed(2)} (${Math.abs(percentage).toFixed(2)}%)`
            };
          } else {
            return {
              type: 'break-even',
              amount: '0.00',
              percentage: '0.00',
              message: 'Break-even: No profit or loss'
            };
          }
        }
      }
      return null;
    }
  },
  methods: {
    onIsFixedSalaryChange(checked) {
      this.form.isFixedSalary = checked;
      if (checked) {
        this.form.payRate = '';
        this.clearFieldError('payRate');
      } else {
        this.form.fixedSalary = '';
        this.clearFieldError('fixedSalary');
      }
    },
    resolveSelectValue(eventOrValue) {
      if (eventOrValue != null && typeof eventOrValue === 'object' && 'target' in eventOrValue) {
        return eventOrValue.target.value;
      }
      return eventOrValue;
    },
    addressTypeDropdownOptions(idx, isNew) {
      const types = this.getAvailableAddressTypes(idx, isNew);
      return [
        { value: '', label: 'Select Type' },
        ...types.map((type) => ({ value: type, label: type })),
      ];
    },
    onSellServiceChange(checked) {
      if (this.isC2CType) {
        this.form.sellService = true;
        return;
      }
      if (this.form.buyService && !checked) {
        this.form.sellService = true;
        return;
      }
      this.form.sellService = checked;
      this.handleServiceChange();
    },
    onBuyServiceChange(checked) {
      if (this.isC2CType) {
        this.form.buyService = true;
        return;
      }
      this.form.buyService = checked;
      if (this.form.buyService) {
        this.form.sellService = true;
      }
      this.handleServiceChange();
    },
    handleTabChange(newTab) {
      this.tabPanelEnter = newTab;
      if (newTab === 'address') {
        this.initializeDefaultAddresses();
      }
    },
    onTabPanelAnimEnd(event, tab) {
      if (event?.animationName !== 'consultant-creation-dialog-tab-switch') return;
      if (this.tabPanelEnter === tab) {
        this.tabPanelEnter = '';
      }
    },
    initializeDefaultAddresses() {
      // If no addresses exist, add two default address rows
      if (this.addresses.length === 0 && this.newAddresses.length === 0) {
        this.newAddresses.push({ type: '', address1: '', address2: '', city: '', state: 'UT', postalCode: '', country: 'US' });
        this.newAddresses.push({ type: '', address1: '', address2: '', city: '', state: 'UT', postalCode: '', country: 'US' });
      }
    },
    switchToTabWithErrors() {
      // Check which tab has validation errors and switch to it
      const basicFields = ['consultantId', 'consultantName', 'consultantType', 'email', 'phone', 'payRate', 'fixedSalary'];
      const serviceFields = ['salesPrice', 'purchasePrice', 'incomeAccountId', 'expenseAccountId', 'standardCost', 'serviceSelection'];
      
      if (basicFields.some((field) => this.validationErrors[field])) {
        this.activeTab = 'basic';
        return;
      }
      if (!this.isFulltimeCompanyType && serviceFields.some((field) => this.validationErrors[field])) {
        this.activeTab = 'service';
        return;
      }
      this.activeTab = 'basic';
    },
    showError(message) {
      this.errorMessage = message;
      this.showErrorModal = true;
    },
    closeErrorModal() {
      this.showErrorModal = false;
      this.errorMessage = '';
    },
    handleOverlayClick(event) {
      if (!this.closeOnOverlayClick) return;
      if (event?.target !== event?.currentTarget) return;
      if (this.hasOpenFloatingLayer()) return;
      this.dismissDialog();
    },
    hasOpenFloatingLayer() {
      return Boolean(
        document.querySelector('.consultant-creation-dialog-modal-overlay .simple-select-wrapper.is-open')
        || document.querySelector('.consultant-creation-dialog-modal-overlay .label-dropdown__menu-panel--teleported')
        || document.querySelector('.consultant-creation-dialog-modal-overlay .label-dropdown__menu-panel:not([style*="display: none"])')
        || this.showAddPaymentTermModal
        || this.showAddInvoiceFrequencyModal
        || this.showAddCustomerModal
        || this.showAddAccountModal
        || this.showAddVendorModal
        || this.showProfitLossModal
        || this.showSuccessModal
        || this.showErrorModal
        || this.showInvoiceFrequencySuccessModal
      );
    },
    handleClose() {
      this.dismissDialog();
    },
    dismissDialog() {
      this.$emit('close');
      this.$emit('cancel');
    },
    navigateToNewAccount() {
      const companyId = this.effectiveCompanyId;
      this.$router.push(`/company/${companyId}/dashboard/new-account`);
    },
    cleanFormData() {
      const cleanedData = { ...this.form };
      
      // Convert empty strings to null for ID fields
      if (cleanedData.consultantId === '') cleanedData.consultantId = null;
      
      // Convert empty strings to null for numeric fields
      if (cleanedData.salesPrice === '') cleanedData.salesPrice = null;
      if (cleanedData.purchasePrice === '') cleanedData.purchasePrice = null;
      if (cleanedData.payRate === '') cleanedData.payRate = null;
      if (cleanedData.fixedSalary === '') cleanedData.fixedSalary = null;
      if (cleanedData.standardCost === '') cleanedData.standardCost = null;
      
      // Convert empty strings to null for ID fields
      if (cleanedData.incomeAccountId === '') cleanedData.incomeAccountId = null;
      if (cleanedData.expenseAccountId === '') cleanedData.expenseAccountId = null;
      if (cleanedData.customerId === '') cleanedData.customerId = null;
      if (cleanedData.paymentTermId === '') cleanedData.paymentTermId = null;
      if (cleanedData.preferredVendorId === '') cleanedData.preferredVendorId = null;
      
      // Convert invoicingFrequencyId to invoicingFrequency (name string)
      // The consultant model uses invoicingFrequency as STRING (name), not ID
      if (cleanedData.invoicingFrequencyId && cleanedData.invoicingFrequencyId !== '') {
        const invoiceFrequency = this.invoiceFrequencies.find(freq => 
          String(freq.id) === String(cleanedData.invoicingFrequencyId) || 
          Number(freq.id) === Number(cleanedData.invoicingFrequencyId)
        );
        
        if (invoiceFrequency) {
          cleanedData.invoicingFrequency = invoiceFrequency.name;
        } else {
          logger.warn(`Invoice frequency with ID ${cleanedData.invoicingFrequencyId} not found in invoice frequencies list`);
          cleanedData.invoicingFrequency = null;
        }
      } else {
        cleanedData.invoicingFrequency = null;
      }
      
      // Remove invoicingFrequencyId as it's not a field in the consultant model
      delete cleanedData.invoicingFrequencyId;
      
      // Ensure numeric fields are properly formatted
      if (cleanedData.salesPrice !== null) cleanedData.salesPrice = Number(cleanedData.salesPrice);
      if (cleanedData.purchasePrice !== null) cleanedData.purchasePrice = Number(cleanedData.purchasePrice);
      if (cleanedData.standardCost !== null) cleanedData.standardCost = Number(cleanedData.standardCost);
      if (!this.showPayRate) {
        cleanedData.payRate = null;
        cleanedData.fixedSalary = null;
        cleanedData.isFixedSalary = false;
      } else if (cleanedData.isFixedSalary) {
        cleanedData.payRate = null;
        cleanedData.fixedSalary = cleanedData.fixedSalary !== null ? Number(cleanedData.fixedSalary) : null;
      } else {
        cleanedData.fixedSalary = null;
        const resolvedPayRate = resolvePayRateFromInput(this.form.payRate, this.form.salesPrice);
        this.form.payRate = resolvedPayRate.value;
        cleanedData.payRate = resolvedPayRate.value !== '' ? Number(resolvedPayRate.value) : null;
      }
      
      // Note: Email and Phone are now required fields, so we don't convert them to null
      // They will be validated before submission

      cleanedData.isFulltimeCompany = this.form.consultantType === 'Fulltime'
        ? Boolean(this.form.isFulltimeCompany)
        : false;

      if (cleanedData.isFulltimeCompany) {
        cleanedData.sellService = false;
        cleanedData.buyService = false;
        cleanedData.salesPrice = null;
        cleanedData.purchasePrice = null;
        cleanedData.standardCost = null;
        cleanedData.incomeAccountId = null;
        cleanedData.expenseAccountId = null;
        cleanedData.preferredVendorId = null;
        cleanedData.salesDescription = null;
        cleanedData.purchaseDescription = null;
        cleanedData.vendorServiceNo = null;
        cleanedData.customerId = null;
        cleanedData.invoicingFrequency = null;
        cleanedData.paymentTermId = null;
        cleanedData.commissionItem = false;
      }

      return cleanedData;
    },
    
    async saveAndClose() {
      // Check for existing email first
      const emailExists = await this.checkEmailExists(this.form.email.trim());
      if (emailExists) {
        this.validationErrors.email = 'This email address is already registered for another consultant.';
        this.errorMessage = 'This email address is already registered for another consultant.';
        this.showErrorModal = true;
        this.activeTab = 'basic';
        return;
      }

      // Validate current tab first
      const currentTabErrors = this.validateCurrentTab();
      if (Object.keys(currentTabErrors).length > 0) {
        // Show errors for current tab only
        this.validationErrors = currentTabErrors;
        const errorMessages = Object.values(currentTabErrors)
          .map(message => `• ${message}`)
          .join('\n');
        
        // Check if there's a Standard Cost vs Sales Price validation error
        const hasCostPriceError = this.validationErrors.standardCost && 
          this.validationErrors.standardCost.includes('cannot exceed Sales Price');
        
        if (hasCostPriceError) {
          this.errorMessage = '⚠️ CANNOT CREATE CONSULTANT ⚠️\n\n' +
            'You must fix the pricing issue before creating this consultant:\n\n' +
            '• ' + this.validationErrors.standardCost;
        } else {
          this.errorMessage = 'Please fix the following issues:\n\n' + errorMessages;
        }
        this.showErrorModal = true;
        return;
      }

      // If current tab is valid, validate entire form
      if (!this.validateForm()) {
        this.switchToTabWithErrors();
        const errorMessages = Object.values(this.validationErrors)
          .map(message => `• ${message}`)
          .join('\n');
        // Check if there's a Standard Cost vs Sales Price validation error
        const hasCostPriceError = this.validationErrors.standardCost && 
          this.validationErrors.standardCost.includes('cannot exceed Sales Price');
        
        if (hasCostPriceError) {
          this.errorMessage = '⚠️ CANNOT CREATE CONSULTANT ⚠️\n\n' +
            'You must fix the pricing issue before creating this consultant:\n\n' +
            '• ' + this.validationErrors.standardCost;
        } else {
          this.errorMessage = 'Please fix the following issues:\n\n' + errorMessages;
        }
        this.showErrorModal = true;
        return;
      }
      if (!this.validateFormData()) return;
      try {
        const companyId = this.effectiveCompanyId;
        const cleanedData = this.cleanFormData();
        const consultantPayload = { ...cleanedData, companyId };
        const consultantRes = await ApiService.post('/customer/consultants', consultantPayload);
        const consultantId = consultantRes.data.id;
        const addressesWithId = this.addresses
          .filter(addr => addr.type)
          .map(addr => ({ ...addr, consultantId }));
        if (addressesWithId.length > 0) {
          await ApiService.post('/customer/consultant-addresses', addressesWithId);
        }
        this.successTitle = 'Success';
        this.successMessage = 'Consultant saved successfully!';
        this.showSuccessModal = true;
        this.isSaveAndNew = false; // Mark as Save and Close
        this.savedConsultant = consultantRes.data; // Store the saved consultant data
        // Close Create Consultant immediately; success modal stays mounted outside overlay
        this.$emit('ok', this.savedConsultant);
        this.$emit('close');
        this.$emit('cancel');
      } catch (err) {
        this.saveSuccess = false;
        let errorMessage = 'Error saving consultant.';
        
        if (err.response?.data?.error) {
          errorMessage = err.response.data.error;
        } else if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.message) {
          errorMessage = err.message;
        }
        
        // Handle specific database errors
        if (errorMessage.includes('invalid input syntax for integer')) {
          errorMessage = 'Please check that all numeric fields contain valid numbers.';
        } else if (errorMessage.includes('not null constraint')) {
          errorMessage = 'Please fill in all required fields.';
        } else if (errorMessage.includes('duplicate key')) {
          errorMessage = 'A consultant with this information already exists.';
        } else if (errorMessage.includes('400') || errorMessage.includes('Bad Request')) {
          errorMessage = 'Validation failed. Please check all required fields and ensure data is in the correct format.';
        }
        
        this.saveError = errorMessage;
        this.errorMessage = errorMessage;
        this.showErrorModal = true;
      }
    },
    async saveAndNew() {
      // Check for existing email first
      const emailExists = await this.checkEmailExists(this.form.email.trim());
      if (emailExists) {
        this.validationErrors.email = 'This email address is already registered for another consultant.';
        this.errorMessage = 'This email address is already registered for another consultant.';
        this.showErrorModal = true;
        this.activeTab = 'basic';
        return;
      }

      // Validate current tab first
      const currentTabErrors = this.validateCurrentTab();
      if (Object.keys(currentTabErrors).length > 0) {
        // Show errors for current tab only
        this.validationErrors = currentTabErrors;
        const errorMessages = Object.values(currentTabErrors)
          .map(message => `• ${message}`)
          .join('\n');
        
        // Check if there's a Standard Cost vs Sales Price validation error
        const hasCostPriceError = this.validationErrors.standardCost && 
          this.validationErrors.standardCost.includes('cannot exceed Sales Price');
        
        if (hasCostPriceError) {
          this.errorMessage = '⚠️ CANNOT CREATE CONSULTANT ⚠️\n\n' +
            'You must fix the pricing issue before creating this consultant:\n\n' +
            '• ' + this.validationErrors.standardCost;
        } else {
          this.errorMessage = 'Please fix the following issues:\n\n' + errorMessages;
        }
        this.showErrorModal = true;
        return;
      }

      // If current tab is valid, validate entire form
      if (!this.validateForm()) {
        this.switchToTabWithErrors();
        const errorMessages = Object.values(this.validationErrors)
          .map(message => `• ${message}`)
          .join('\n');
        // Check if there's a Standard Cost vs Sales Price validation error
        const hasCostPriceError = this.validationErrors.standardCost && 
          this.validationErrors.standardCost.includes('cannot exceed Sales Price');
        
        if (hasCostPriceError) {
          this.errorMessage = '⚠️ CANNOT CREATE CONSULTANT ⚠️\n\n' +
            'You must fix the pricing issue before creating this consultant:\n\n' +
            '• ' + this.validationErrors.standardCost;
        } else {
          this.errorMessage = 'Please fix the following issues:\n\n' + errorMessages;
        }
        this.showErrorModal = true;
        return;
      }
      if (!this.validateFormData()) return;
      try {
        const companyId = this.effectiveCompanyId;
        const cleanedData = this.cleanFormData();
        const consultantPayload = { ...cleanedData, companyId };
        const consultantRes = await ApiService.post('/customer/consultants', consultantPayload);
        const consultantId = consultantRes.data.id;
        const addressesWithId = this.addresses
          .filter(addr => addr.type)
          .map(addr => ({ ...addr, consultantId }));
        if (addressesWithId.length > 0) {
          await ApiService.post('/customer/consultant-addresses', addressesWithId);
        }
        this.successTitle = 'Success';
        this.successMessage = 'Consultant saved successfully!';
        this.showSuccessModal = true;
        this.isSaveAndNew = false; // Mark as Save and Close
        this.savedConsultant = consultantRes.data; // Store the saved consultant data
        
      } catch (err) {
        this.saveSuccess = false;
        let errorMessage = 'Error saving consultant.';
        
        if (err.response?.data?.error) {
          errorMessage = err.response.data.error;
        } else if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.message) {
          errorMessage = err.message;
        }
        
        // Handle specific database errors
        if (errorMessage.includes('invalid input syntax for integer')) {
          errorMessage = 'Please check that all numeric fields contain valid numbers.';
        } else if (errorMessage.includes('not null constraint')) {
          errorMessage = 'Please fill in all required fields.';
        } else if (errorMessage.includes('duplicate key')) {
          errorMessage = 'A consultant with this information already exists.';
        } else if (errorMessage.includes('400') || errorMessage.includes('Bad Request')) {
          errorMessage = 'Validation failed. Please check all required fields and ensure data is in the correct format.';
        }
        
        this.saveError = errorMessage;
        this.showSaveModal = true;
      }
    },
    closeSuccessModal() {
      this.showSuccessModal = false;
      this.successMessage = '';
      this.savedConsultant = null;
      this.resetForm();
    },
    cancel() {
      this.$emit('cancel');
    },
    resetForm() {
      this.form = {
        consultantId: '',
        consultantName: '',
        consultantType: 'Fulltime',
        isFulltimeCompany: false,
        email: '',
        phone: '',
        sellService: true,
        salesDescription: '',
        salesPrice: '',
        incomeAccountId: '',
        commissionItem: false,
        standardCost: '',
        active: true,
        customerId: '',
        invoicingFrequencyId: '',
        paymentTermId: '',
        buyService: false, // Only enabled for C2C consultants
        purchaseDescription: '',
        purchasePrice: '',
        isFixedSalary: false,
        payRate: '',
        fixedSalary: '',
        expenseAccountId: '',
        preferredVendorId: '',
        vendorServiceNo: '',
      };
      this.addresses = [];
      this.newAddresses = [];
      this.editingAddressIdx = null;
      // Clear all validation errors when resetting
      this.validationErrors = {};
      // Reset payment term tracking
      this.previousPaymentTerm = '';
      // Reset vendor tracking
      this.previousPreferredVendorId = null;
      // Reset auto-selection flags
      this.isPaymentTermsAutoSelected = false;
      this.isInvoiceFrequencyAutoSelected = false;
      // Reset customer tracking
      this.previousCustomerId = null;
      // Clear consultant ID validation state
      this.consultantIdExists = false;
      this.checkingConsultantId = false;
      clearTimeout(this.consultantIdCheckTimeout);
    },
    async fetchCompanyAccounts() {
      this.loadingAccounts = true;
      this.accountsError = '';
      try {
        const companyId = this.effectiveCompanyId;
        const res = await ApiService.getCompanyAccounts(companyId);
        const accounts = res.data.accounts || [];
        this.incomeAccounts = accounts.filter(a => a.accountType === 'Income' || a.accountType === 'Other Income');
        this.expenseAccounts = accounts.filter(a => a.accountType === 'Expense' || a.accountType === 'Other Expense' || a.accountType === 'Cost of Goods Sold');
      } catch (error) {
        this.accountsError = 'Failed to load accounts.';
        this.incomeAccounts = [];
        this.expenseAccounts = [];
      } finally {
        this.loadingAccounts = false;
      }
    },
    handleIncomeAccountChange(eventOrValue) {
      const selected = this.resolveSelectValue(eventOrValue);
      if (selected === '__add_new__') {
        this.showAddAccountModal = true;
        // Reset the select to previous value after showing modal
        this.$nextTick(() => {
          this.form.incomeAccountId = this.previousIncomeAccountId;
        });
      } else {
        this.form.incomeAccountId = selected;
        this.previousIncomeAccountId = selected;
        this.clearFieldError('incomeAccountId');
      }
    },
    handleExpenseAccountChange(eventOrValue) {
      const selected = this.resolveSelectValue(eventOrValue);
      if (selected === '__add_new__') {
        this.showAddAccountModal = true;
        // Reset the select to previous value after showing modal
        this.$nextTick(() => {
          this.form.expenseAccountId = this.previousExpenseAccountId;
        });
      } else {
        this.form.expenseAccountId = selected;
        this.previousExpenseAccountId = selected;
        this.clearFieldError('expenseAccountId');
      }
    },
    
    formatNumericInput(field) {
      if (field === 'payRate') {
        const resolved = resolvePayRateFromInput(this.form.payRate, this.form.salesPrice);
        if (resolved.error) {
          this.validationErrors.payRate = resolved.error;
          return;
        }
        this.form.payRate = resolved.value;
        this.clearFieldError('payRate');
        this.validateNumericFieldOnBlur(field);
        return;
      }
      let value = this.form[field];
      
      // Handle empty or null values
      if (!value || value === '') {
        this.form[field] = '';
        this.clearFieldError(field);
        return;
      }
      
      // Convert to string and remove any non-numeric characters except decimal point
      value = value.toString().replace(/[^\d.]/g, '');
      
      // Handle multiple decimal points - keep only the first one
      const parts = value.split('.');
      if (parts.length > 2) {
        value = parts[0] + '.' + parts.slice(1).join('');
      }
      
      // Remove leading zeros but keep single zero
      if (parts[0] && parts[0].length > 1) {
        value = value.replace(/^0+(?=\d)/, '');
      }
      
      // Limit to 2 decimal places
      if (parts.length === 2 && parts[1].length > 2) {
        value = parts[0] + '.' + parts[1].substring(0, 2);
      }
      
      // Prevent starting with decimal point
      if (value.startsWith('.')) {
        value = '0' + value;
      }
      
      this.form[field] = value;
      
      // Validate the field after formatting
      this.validateNumericFieldOnBlur(field);
    },
    
    validateNumericFieldOnBlur(field) {
      const value = this.form[field];
      let fieldName = '';
      let isRequired = false;
      
      // Determine field name and requirements based on field parameter
      switch(field) {
        case 'salesPrice':
          fieldName = 'Sales Price';
          isRequired = this.form.sellService;
          break;
        case 'purchasePrice':
          fieldName = 'Purchase Price';
          isRequired = this.form.buyService;
          break;
        case 'standardCost':
          fieldName = 'Standard Cost';
          isRequired = true; // Always required
          break;
        case 'payRate':
          fieldName = 'Pay Rate';
          isRequired = false;
          break;
        case 'fixedSalary':
          fieldName = 'Fixed Salary';
          isRequired = false;
          break;
        default:
          fieldName = field;
      }
      
      // Clear existing field-specific error
      delete this.validationErrors[field];
      
      // Handle empty values
      if (!value || value.trim() === '') {
        if (isRequired) {
          this.validationErrors[field] = `${fieldName} is required.`;
        }
        this.validateCostPriceRelationship();
        return;
      }
      
      // Validate numeric format and value
      const numValue = Number(value);
      if (isNaN(numValue)) {
        this.validationErrors[field] = `${fieldName} must be a valid number.`;
      } else if (numValue < 0) {
        this.validationErrors[field] = `${fieldName} cannot be negative.`;
      } else if (numValue === 0 && isRequired) {
        this.validationErrors[field] = `${fieldName} must be greater than zero.`;
      }
      
      // Re-validate relationships after individual field validation
      this.validateCostPriceRelationship();
    },
    
    clearFieldError(field) {
      if (this.validationErrors[field]) {
        delete this.validationErrors[field];
      }
      // Re-validate all price relationships when any field changes
      this.validateCostPriceRelationship();
    },
    
    async handleConsultantIdInput() {
      this.consultantIdExists = false;
      
      // Only check if consultant ID is not empty and company ID exists
      if (!this.form.consultantId || !this.form.consultantId.trim()) {
        return;
      }
      
      const companyId = this.effectiveCompanyId;
      if (!companyId) {
        return;
      }
      
      // Debounce the API call
      clearTimeout(this.consultantIdCheckTimeout);
      this.checkingConsultantId = true;
      
      this.consultantIdCheckTimeout = setTimeout(async () => {
        try {
          const response = await customerService.checkConsultantIdExists(this.form.consultantId.trim(), companyId);
          this.consultantIdExists = response.data.exists;
        } catch (error) {
          logger.error('Error checking consultant ID:', error);
          // Don't show error to user for validation check failures
        } finally {
          this.checkingConsultantId = false;
        }
      }, 500); // 500ms debounce
    },

    handlePriceInput(field) {
      // Clear field-specific error on input
      this.clearFieldError(field);
      
      // Basic real-time validation for common issues
      const value = this.form[field];
      if (value && value !== '') {
        // Check for obviously invalid characters
        const allowedPattern = field === 'payRate' ? isAllowedPayRateTyping(value) : /^[\d.]*$/.test(value);
        if (!allowedPattern) {
          let fieldName = '';
          switch(field) {
            case 'salesPrice': fieldName = 'Sales Price'; break;
            case 'purchasePrice': fieldName = 'Purchase Price'; break;
            case 'standardCost': fieldName = 'Standard Cost'; break;
            case 'payRate': fieldName = 'Pay Rate'; break;
            case 'fixedSalary': fieldName = 'Fixed Salary'; break;
          }
          this.validationErrors[field] = field === 'payRate'
            ? 'Pay Rate can only contain numbers, a decimal, and %.'
            : `${fieldName} can only contain numbers and decimal points.`;
        }
      }
      
      // Trigger relationship validation after a short delay to avoid too frequent updates
      clearTimeout(this.priceValidationTimeout);
      this.priceValidationTimeout = setTimeout(() => {
        this.validateCostPriceRelationship();
      }, 300);
    },

    validateCostPriceRelationship() {
      // Clear previous relationship errors
      delete this.validationErrors.standardCost;
      delete this.validationErrors.salesPrice;
      delete this.validationErrors.purchasePrice;
      
      // Get numeric values for comparison
      const salesPrice = this.form.salesPrice ? Number(this.form.salesPrice) : null;
      const purchasePrice = this.form.purchasePrice ? Number(this.form.purchasePrice) : null;
      const standardCost = this.form.standardCost ? Number(this.form.standardCost) : null;
      
      // Only validate if values are valid numbers
      if (isNaN(salesPrice)) delete this.validationErrors.salesPrice;
      if (isNaN(purchasePrice)) delete this.validationErrors.purchasePrice;
      if (isNaN(standardCost)) delete this.validationErrors.standardCost;
      
      // Rule 1: Standard Cost cannot exceed Sales Price (when selling)
      if (this.form.sellService && salesPrice !== null && standardCost !== null && 
          !isNaN(salesPrice) && !isNaN(standardCost) && standardCost > salesPrice) {
        this.validationErrors.standardCost = `Standard Cost (${standardCost.toFixed(2)}) cannot exceed Sales Price (${salesPrice.toFixed(2)})`;
      }
      
      // Rule 2: Sales Price should be higher than Purchase Price (when both selling and buying)
      if (this.form.sellService && this.form.buyService && 
          salesPrice !== null && purchasePrice !== null &&
          !isNaN(salesPrice) && !isNaN(purchasePrice) && salesPrice <= purchasePrice) {
        this.validationErrors.salesPrice = `Sales Price (${salesPrice.toFixed(2)}) should be higher than Purchase Price (${purchasePrice.toFixed(2)})`;
      }
      
      // Rule 3: Standard Cost should be within reasonable range when both prices exist
      if (this.form.sellService && this.form.buyService && 
          salesPrice !== null && purchasePrice !== null && standardCost !== null &&
          !isNaN(salesPrice) && !isNaN(purchasePrice) && !isNaN(standardCost)) {
        const minPrice = Math.min(salesPrice, purchasePrice);
        const maxPrice = Math.max(salesPrice, purchasePrice);
        
        if (standardCost < minPrice) {
          this.validationErrors.standardCost = `Standard Cost (${standardCost.toFixed(2)}) is too low. It should be at least ${minPrice.toFixed(2)} to cover your costs.`;
        } else if (standardCost > maxPrice) {
          this.validationErrors.standardCost = `Standard Cost (${standardCost.toFixed(2)}) is too high. It should not exceed ${maxPrice.toFixed(2)} to maintain profitability.`;
        }
      }
    },
    
    async checkEmailExists(email) {
      try {
        const companyId = this.effectiveCompanyId;
        const response = await ApiService.get(`/customer/consultants/check-email?email=${encodeURIComponent(email)}&companyId=${companyId}`);
        return response.data.exists;
      } catch (error) {
        logger.error('Error checking email:', error);
        return false;
      }
    },

    async validateEmail() {
      const email = this.form.email.trim();
      
      // Clear existing email error
      delete this.validationErrors.email;
      
      // Basic email format validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        this.validationErrors.email = 'Please enter a valid email address.';
        return;
      }
      
      // Check if email exists
      const exists = await this.checkEmailExists(email);
      if (exists) {
        this.validationErrors.email = 'This email address is already registered for another consultant.';
        this.errorMessage = 'This email address is already registered for another consultant.';
        this.showErrorModal = true;
      }
    },
    
    handleConsultantTypeChange() {
      this.clearFieldError('consultantType');
      if (this.consultantTypeSelection === 'Fulltime-Company') {
        this.form.consultantType = 'Fulltime';
        this.form.isFulltimeCompany = true;
        this.form.sellService = false;
        this.form.buyService = false;
        this.form.salesPrice = '';
        this.form.purchasePrice = '';
        this.form.standardCost = '';
        this.form.incomeAccountId = '';
        this.form.expenseAccountId = '';
        this.form.preferredVendorId = '';
        this.form.salesDescription = '';
        this.form.purchaseDescription = '';
        this.form.customerId = '';
        this.form.invoicingFrequencyId = '';
        this.form.paymentTermId = '';
        this.form.commissionItem = false;
        this.isInvoiceFrequencyAutoSelected = false;
        this.isPaymentTermsAutoSelected = false;
        if (this.activeTab === 'service') {
          this.activeTab = 'basic';
        }
      } else if (this.consultantTypeSelection === 'Fulltime-Client') {
        this.form.consultantType = 'Fulltime';
        this.form.isFulltimeCompany = false;
        this.form.sellService = true;
        this.form.buyService = false;
      } else if (this.form.consultantType !== 'Fulltime') {
        this.form.isFulltimeCompany = false;
      }
      if (this.form.consultantType === 'C2C') {
        this.form.sellService = true;
        this.form.buyService = true;
        this.form.payRate = '';
      } else if (!this.isFulltimeCompanyType) {
        this.form.buyService = false;
      }
    },
    
    handleServiceChange() {
      // Clear service selection error when user changes service selection
      if (this.validationErrors.serviceSelection) {
        delete this.validationErrors.serviceSelection;
      }
      
      // Clear related field errors when service is disabled
      if (!this.form.sellService) {
        delete this.validationErrors.salesPrice;
        delete this.validationErrors.incomeAccountId;
      }
      if (!this.form.buyService) {
        delete this.validationErrors.purchasePrice;
        delete this.validationErrors.expenseAccountId;
        delete this.validationErrors.preferredVendorId;
      }
    },
    handleNext() {
      // Validate current tab first
      const currentTabErrors = this.validateCurrentTab();
      
      if (Object.keys(currentTabErrors).length > 0) {
        // Show errors for current tab only
        this.validationErrors = currentTabErrors;
        const errorMessages = Object.values(currentTabErrors)
          .map(message => `• ${message}`)
          .join('\n');
        
        // Check if there's a Standard Cost vs Sales Price validation error
        const hasCostPriceError = this.validationErrors.standardCost && 
          this.validationErrors.standardCost.includes('cannot exceed Sales Price');
        
        if (hasCostPriceError) {
          this.errorMessage = '⚠️ CANNOT CREATE CONSULTANT ⚠️\n\n' +
            'You must fix the pricing issue before creating this consultant:\n\n' +
            '• ' + this.validationErrors.standardCost;
        } else {
          this.errorMessage = 'Please fix the following issues:\n\n' + errorMessages;
        }
        this.showErrorModal = true;
        return;
      }

      // If no errors, move to next tab
      const tabs = this.visibleTabs.map((t) => t.id);
      const currentIndex = tabs.indexOf(this.activeTab);
      if (currentIndex < tabs.length - 1) {
        this.activeTab = tabs[currentIndex + 1];
        
        // Initialize default addresses when moving to address tab
        if (tabs[currentIndex + 1] === 'address') {
          this.initializeDefaultAddresses();
        }
      }
    },

    validateNumericField(value, fieldName, isRequired = false, minValue = 0) {
      if (isRequired && (value === '' || value === null || value === undefined)) {
        return `${fieldName} is required.`;
      }
      if (value !== '' && value !== null && value !== undefined) {
        const numValue = Number(value);
        if (isNaN(numValue)) {
          return `${fieldName} must be a valid number.`;
        }
        if (numValue < minValue) {
          return minValue === 0 ? `${fieldName} cannot be negative.` : `${fieldName} must be at least ${minValue}.`;
        }
        if (minValue === 0 && numValue === 0) {
          return `${fieldName} must be greater than zero.`;
        }
      }
      return null;
    },

    validateCurrentTab() {
      const errors = {};
      const salesEnabled = this.form.sellService;
      const buyEnabled = this.form.buyService;
      
      switch(this.activeTab) {
        case 'basic':
          // Validate Basic Information tab
          if (!this.form.consultantId || this.form.consultantId.trim() === '') {
            errors.consultantId = 'Consultant ID is required.';
          } else if (this.form.consultantId.trim().length < 2) {
            errors.consultantId = 'Consultant ID must be at least 2 characters long.';
          }
          if (this.consultantIdExists) {
            errors.consultantId = 'This Consultant ID already exists in this company.';
          }

          if (!this.form.consultantName || this.form.consultantName.trim() === '') {
            errors.consultantName = 'Consultant Name is required.';
          } else if (this.form.consultantName.trim().length < 2) {
            errors.consultantName = 'Consultant Name must be at least 2 characters long.';
          }

          // Consultant Type validation (required field)
          if (!this.form.consultantType || this.form.consultantType === '') {
            errors.consultantType = 'Consultant Type is required.';
          }

          // Email validation (required field)
          if (!this.form.email || this.form.email.trim() === '') {
            errors.email = 'Email is required.';
          } else {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(this.form.email.trim())) {
              errors.email = 'Please enter a valid email address.';
            }
          }

          // Phone validation (required field)
          if (!this.form.phone || this.form.phone.trim() === '') {
            errors.phone = 'Phone number is required.';
          } else {
            const phoneRegex = /^[+]?[1-9]\d{0,15}$/;
            if (!phoneRegex.test(this.form.phone.trim().replace(/[\s\-()]/g, ''))) {
              errors.phone = 'Please enter a valid phone number.';
            }
          }

          if (this.isFulltimeCompanyType) {
            if (this.form.isFixedSalary) {
              const fixedSalaryError = this.validateNumericField(this.form.fixedSalary, 'Fixed Salary', true, 0);
              if (fixedSalaryError) errors.fixedSalary = fixedSalaryError;
            } else {
              const payRateError = this.validateNumericField(this.form.payRate, 'Pay Rate', true, 0);
              if (payRateError) errors.payRate = payRateError;
            }
          }
          break;

        case 'service':
          if (this.isFulltimeCompanyType) {
            break;
          }
          // Service Type Validation
          if (!salesEnabled && !buyEnabled) {
            errors.serviceSelection = 'Please select at least one service type (sell or buy).';
          }

          {
            // Sales Service Validation
            let salesPriceError;
            let purchasePriceError;
            let standardCostError;

            if (salesEnabled) {
              salesPriceError = this.validateNumericField(this.form.salesPrice, 'Sales Price', true, 0);
              if (salesPriceError) {
                errors.salesPrice = salesPriceError;
              }
              if (!this.form.incomeAccountId) {
                errors.incomeAccountId = 'Please select an income account for selling service.';
              }
            }

            // Buy Service Validation
            if (buyEnabled) {
              purchasePriceError = this.validateNumericField(this.form.purchasePrice, 'Purchase Price', true, 0);
              if (purchasePriceError) {
                errors.purchasePrice = purchasePriceError;
              }
              if (!this.form.expenseAccountId) {
                errors.expenseAccountId = 'Please select an expense account for buying service.';
              }
              if (!this.form.preferredVendorId) {
                errors.preferredVendorId = 'Please select a preferred vendor for buying service.';
              }
            }

            // Standard Cost validation
            standardCostError = this.validateNumericField(this.form.standardCost, 'Standard Cost', true, 0);
            if (standardCostError) {
              errors.standardCost = standardCostError;
            }
          }
          break;

        case 'address':
          // Address validation
          if (this.addresses.length > 0) {
            this.addresses.forEach((addr, index) => {
              if (!addr.type) {
                errors[`address_${index}_type`] = 'Please select an address type.';
              }
              if (!addr.address1) {
                errors[`address_${index}_address1`] = 'Address Line 1 is required.';
              }
              if (!addr.city) {
                errors[`address_${index}_city`] = 'City is required.';
              }
              if (!addr.state) {
                errors[`address_${index}_state`] = 'State is required.';
              }
              if (!addr.postalCode) {
                errors[`address_${index}_postalCode`] = 'Postal Code is required.';
              }
              if (!addr.country) {
                errors[`address_${index}_country`] = 'Country is required.';
              }
            });
          }
          break;
      }

      return errors;
    },

    validateForm() {
      const errors = {};
      const salesEnabled = this.form.sellService;
      const buyEnabled = this.form.buyService;
      
      
      
      // Helper function to validate numeric fields
      const validateNumericField = (value, fieldName, isRequired = false, minValue = 0) => {
        if (isRequired && (value === '' || value === null || value === undefined)) {
          return `${fieldName} is required.`;
        }
        if (value !== '' && value !== null && value !== undefined) {
          const numValue = Number(value);
          if (isNaN(numValue)) {
            return `${fieldName} must be a valid number.`;
          }
          if (numValue < minValue) {
            return minValue === 0 ? `${fieldName} cannot be negative.` : `${fieldName} must be at least ${minValue}.`;
          }
          if (minValue === 0 && numValue === 0) {
            return `${fieldName} must be greater than zero.`;
          }
        }
        return null;
      };

      // Consultant ID validation
      if (!this.form.consultantId || this.form.consultantId.trim() === '') {
        errors.consultantId = 'Consultant ID is required.';
      } else if (this.form.consultantId.trim().length < 2) {
        errors.consultantId = 'Consultant ID must be at least 2 characters long.';
      }
      if (this.consultantIdExists) {
        errors.consultantId = 'This Consultant ID already exists in this company.';
      }

      // Consultant Name validation
      if (!this.form.consultantName || this.form.consultantName.trim() === '') {
        errors.consultantName = 'Consultant Name is required.';
      } else if (this.form.consultantName.trim().length < 2) {
        errors.consultantName = 'Consultant Name must be at least 2 characters long.';
      }

      // Consultant Type validation
      if (!this.form.consultantType || this.form.consultantType === '') {
        errors.consultantType = 'Consultant Type is required.';
      }

      if (this.isFulltimeCompanyType) {
        if (this.form.isFixedSalary) {
          const fixedSalaryError = validateNumericField(this.form.fixedSalary, 'Fixed Salary', true, 0);
          if (fixedSalaryError) errors.fixedSalary = fixedSalaryError;
        } else {
          const payRateError = validateNumericField(this.form.payRate, 'Pay Rate', true, 0);
          if (payRateError) errors.payRate = payRateError;
        }
      } else {
        // Sales Price validation
        if (salesEnabled) {
          const salesPriceError = validateNumericField(this.form.salesPrice, 'Sales Price', true, 0);
          if (salesPriceError) {
            errors.salesPrice = salesPriceError;
          }
        }

        // Purchase Price validation
        if (buyEnabled) {
          const purchasePriceError = validateNumericField(this.form.purchasePrice, 'Purchase Price', true, 0);
          if (purchasePriceError) {
            errors.purchasePrice = purchasePriceError;
          }
        }

        // Standard Cost validation
        const standardCostError = validateNumericField(this.form.standardCost, 'Standard Cost', true, 0);
        if (standardCostError) {
          errors.standardCost = standardCostError;
        }

        // Income Account validation
        if (salesEnabled && (!this.form.incomeAccountId || this.form.incomeAccountId === '')) {
          errors.incomeAccountId = 'Please select an Income Account for the service you are selling.';
        }

        // Expense Account validation
        if (buyEnabled && (!this.form.expenseAccountId || this.form.expenseAccountId === '')) {
          errors.expenseAccountId = 'Please select an Expense Account for the service you are buying.';
        }

        // Preferred Vendor validation
        if (buyEnabled && (!this.form.preferredVendorId || this.form.preferredVendorId === '')) {
          errors.preferredVendorId = 'Please select a Preferred Vendor for the service you are buying.';
        }

        // Business Logic Validation - Sales Price vs Purchase Price
        if (salesEnabled && buyEnabled && !errors.salesPrice && !errors.purchasePrice) {
          const salesPrice = Number(this.form.salesPrice);
          const purchasePrice = Number(this.form.purchasePrice);
          if (salesPrice < purchasePrice) {
            errors.salesPrice = `Sales Price (${salesPrice}) should be higher than Purchase Price (${purchasePrice})`;
          }
        }

        // Business Logic Validation - Standard Cost Range
        if (salesEnabled && buyEnabled && !errors.salesPrice && !errors.purchasePrice && !errors.standardCost) {
          const salesPrice = Number(this.form.salesPrice);
          const purchasePrice = Number(this.form.purchasePrice);
          const standardCost = Number(this.form.standardCost);
          const minVal = Math.min(salesPrice, purchasePrice);
          const maxVal = Math.max(salesPrice, purchasePrice);
          if (standardCost < minVal) {
            errors.standardCost = `Standard Cost (${standardCost}) is too low. It should be at least ${minVal} to cover your costs.`;
          } else if (standardCost > maxVal) {
            errors.standardCost = `Standard Cost (${standardCost}) is too high. It should not exceed ${maxVal} to maintain profitability.`;
          }
        }

        // Business Logic Validation - Standard Cost vs Sales Price
        if (salesEnabled && this.form.salesPrice && this.form.standardCost) {
          const salesPrice = Number(this.form.salesPrice);
          const standardCost = Number(this.form.standardCost);
          if (!isNaN(salesPrice) && !isNaN(standardCost) && standardCost > salesPrice) {
            errors.standardCost = `Standard Cost (${standardCost}) cannot exceed Sales Price (${salesPrice})`;
          }
        }

        // Additional Business Logic - At least one service should be enabled
        if (!salesEnabled && !buyEnabled) {
          errors.serviceSelection = 'Please select at least one service type (sell or buy) to continue.';
        }
      }

      // Email validation (required field)
      if (!this.form.email || this.form.email.trim() === '') {
        errors.email = 'Email is required.';
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(this.form.email.trim())) {
          errors.email = 'Please enter a valid email address.';
        }
      }

      // Phone validation (required field)
      if (!this.form.phone || this.form.phone.trim() === '') {
        errors.phone = 'Phone number is required.';
      } else {
        const phoneRegex = /^[+]?[1-9]\d{0,15}$/;
        if (!phoneRegex.test(this.form.phone.trim().replace(/[\s\-()]/g, ''))) {
          errors.phone = 'Please enter a valid phone number.';
        }
      }
      
      // Additional validation: ensure all required fields have values
      if (!this.form.consultantName || this.form.consultantName.trim() === '') {
        errors.consultantName = 'Consultant Name is required.';
      }
      
      if (!this.isFulltimeCompanyType) {
        // Check if at least one service is selected and has required fields
        if (salesEnabled && (!this.form.salesPrice || this.form.salesPrice === '')) {
          errors.salesPrice = 'Sales Price is required when selling services.';
        }

        if (buyEnabled && (!this.form.purchasePrice || this.form.purchasePrice === '')) {
          errors.purchasePrice = 'Purchase Price is required when buying services.';
        }

        if (!this.form.standardCost || this.form.standardCost === '') {
          errors.standardCost = 'Standard Cost is required.';
        }
      }

      this.validationErrors = errors;
       
       // Return true if no errors, false if there are errors
      return Object.keys(errors).length === 0;
    },
    async fetchCustomers() {
      try {
        const companyId = this.effectiveCompanyId;
        const res = await ApiService.get(`/customer/customers?companyId=${companyId}&active=true`);
        this.customers = Array.isArray(res.data) ? res.data : [];
        
        // Ensure customers have payment terms and invoice frequency information
        // The API should include paymentTermId and invoiceFrequencyId in the customer data
        this.customers.forEach(customer => {
          if (customer.paymentTermId) {
            // Validate that the payment term exists in our payment terms list
            const paymentTerm = this.paymentTerms.find(term => term.id == customer.paymentTermId);
            if (!paymentTerm) {
              logger.warn(`Customer ${customer.customerName} has payment term ID ${customer.paymentTermId} that doesn't exist in current payment terms list`);
            }
          }
          if (customer.invoiceFrequencyId) {
            // Validate that the invoice frequency exists in our invoice frequencies list
            const invoiceFrequency = this.invoiceFrequencies.find(freq => freq.id == customer.invoiceFrequencyId);
            if (!invoiceFrequency) {
              logger.warn(`Customer ${customer.customerName} has invoice frequency ID ${customer.invoiceFrequencyId} that doesn't exist in current invoice frequencies list`);
            }
          }
        });
        return this.customers;
      } catch (error) {
        logger.error('Error fetching customers:', error);
        this.customers = [];
        return [];
      }
    },
    async fetchVendors() {
      try {
        const companyId = this.effectiveCompanyId;
        const response = await ApiService.get(`/vendor/vendors?companyId=${companyId}&active=true`);
        this.vendors = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        logger.error('Error fetching vendors:', error);
        this.vendors = [];
      }
    },
    getUsedAddressTypes() {
      return this.addresses.map(addr => addr.type).filter(Boolean);
    },
    addNewAddressRow() {
      this.newAddresses.push({ type: '', address1: '', address2: '', city: '', state: 'UT', postalCode: '', country: 'US' });
    },
    saveNewAddress(idx) {
      const na = this.newAddresses[idx];
      
      // First check required fields
      if (!na.type || !na.address1 || !na.city || !na.state || !na.postalCode || !na.country) {
        if (!na.type) {
          this.showError('Please select an address type before adding.');
        } else {
          this.showError('Please fill in all address fields before adding.');
        }
        return;
      }
      
      // Check for duplicate address type
      const allTypes = this.addresses.map(a => a.type).concat(this.newAddresses.map(a => a.type));
      if (allTypes.filter(t => t === na.type).length > 1) {
        this.showError('This address type has already been added.');
        return;
      }
      this.addresses = [...this.addresses, na];
      this.newAddresses.splice(idx, 1);
    },
    cancelNewAddress(idx) {
      this.newAddresses.splice(idx, 1);
    },
    editAddress(idx) {
      this.editingAddressIdx = idx;
    },
    updateAddress(idx) {
      const addr = this.addresses[idx];
      // Prevent duplicate address type (except for itself)
      const otherTypes = this.addresses.map((a, i) => i !== idx ? a.type : null).concat(this.newAddresses.map(a => a.type));
      if (otherTypes.includes(addr.type)) {
        this.showError('This address type has already been added.');
        return;
      }
      if (!addr.type || !addr.address1 || !addr.city || !addr.state || !addr.postalCode || !addr.country) {
        if (!addr.type) {
          this.showError('Please select an address type before updating.');
        } else {
          this.showError('Please fill in all address fields before updating.');
        }
        return;
      }
      this.editingAddressIdx = null;
      // Optionally, show a toast or feedback
    },
    cancelEditAddress() {
      this.editingAddressIdx = null;
      // Optionally, reload the address from original if you keep a backup
    },
    deleteAddress(idx) {
      this.addresses.splice(idx, 1);
    },
    async fetchPaymentTerms() {
      try {
        const companyId = this.effectiveCompanyId;
        const res = await ApiService.get(`/company/payment-terms?companyId=${companyId}`);
        this.paymentTerms = Array.isArray(res.data.paymentTerms) ? res.data.paymentTerms : [];
        
        // Don't update previousPaymentTerm here as it might interfere with selection logic
        // this.previousPaymentTerm = this.form.paymentTermId;
        
        // Payment terms fetched successfully
        return this.paymentTerms;
      } catch (err) {
        logger.error('Error fetching payment terms:', err);
        this.paymentTerms = [];
        return [];
      }
    },
    getAvailableAddressTypes(idx, isNew) {
      const allTypes = ['LCA Address', 'Living Address'];
      let usedTypes = this.addresses.map((a, i) => (isNew ? a.type : (i !== idx ? a.type : null))).filter(Boolean);
      if (isNew) {
        usedTypes = usedTypes.concat(this.newAddresses.map((a, i) => (i !== idx ? a.type : null)).filter(Boolean));
      }
      let currentType = isNew ? this.newAddresses[idx]?.type : this.addresses[idx]?.type;
      return allTypes.filter(type => !usedTypes.includes(type) || type === currentType);
    },
    
    handlePaymentTermChange(eventOrValue) {
      const selected = this.resolveSelectValue(eventOrValue);
      if (selected === '__add_new__') {
        this.showAddPaymentTermModal = true;
        // Store current value before showing modal
        const currentValue = this.form.paymentTermId;
        // Reset the select to previous value after showing modal
        this.$nextTick(() => {
          this.form.paymentTermId = currentValue;
        });
      } else {
        this.form.paymentTermId = selected;
        this.previousPaymentTerm = selected;
        // Reset auto-selection flag when user manually changes payment terms
        this.isPaymentTermsAutoSelected = false;
        // Payment term selection updated
      }
    },
    
    async onAddPaymentTermOk(paymentTerm) {
      this.showAddPaymentTermModal = false;
      
      try {
        // Refresh payment terms list
        await this.fetchPaymentTerms();
        
        // Auto-select the newly created payment term
        if (paymentTerm) {
          // Handle different possible data structures
          const termId = paymentTerm.id || paymentTerm.paymentTermId || paymentTerm;
          
          if (termId) {
            // Use $nextTick to ensure the DOM is updated with new options
            this.$nextTick(() => {
              this.form.paymentTermId = String(termId);
              this.previousPaymentTerm = String(termId);
              
              // Verify the term was actually added to the list
              const termExists = this.paymentTerms.find(term => 
                String(term.id) === String(termId)
              );
              
              if (!termExists) {
                logger.warn('Newly created payment term not found in list, refetching...');
                // If term not found, try fetching again after a short delay
                setTimeout(async () => {
                  await this.fetchPaymentTerms();
                  this.$nextTick(() => {
                    this.form.paymentTermId = String(termId);
                    this.previousPaymentTerm = String(termId);
                  });
                }, 500);
              }
            });
            
            // Show success message if toast is available
            if (this.$toast) {
              this.$toast.success('Payment term created and selected successfully!');
            }
          } else {
            logger.warn('Payment term created but ID not found in response:', paymentTerm);
            if (this.$toast) {
              this.$toast.success('Payment term created successfully!');
            }
          }
        }
      } catch (error) {
        logger.error('Error handling new payment term:', error);
        if (this.$toast) {
          this.$toast.error('Payment term created but there was an issue selecting it. Please select it manually.');
        }
      }
    },
    
    onAddPaymentTermCancel() {
      this.showAddPaymentTermModal = false;
      // Reset to previous value when cancelled
      this.form.paymentTermId = this.previousPaymentTerm;
    },

    async fetchInvoiceFrequencies() {
      try {
        const companyId = this.effectiveCompanyId;
        const frequencies = await CompanyService.getAllInvoiceFrequencies(companyId);
        this.invoiceFrequencies = Array.isArray(frequencies) ? frequencies : [];
        this.previousInvoicingFrequencyId = this.form.invoicingFrequencyId;
        return this.invoiceFrequencies;
      } catch (err) {
        this.invoiceFrequencies = [];
        return [];
      }
    },

    handleInvoicingFrequencyChange(eventOrValue) {
      const selected = this.resolveSelectValue(eventOrValue);
      if (selected === '__add_new__') {
        this.showAddInvoiceFrequencyModal = true;
        // Reset the select to previous value after showing modal
        this.$nextTick(() => {
          this.form.invoicingFrequencyId = this.previousInvoicingFrequencyId;
        });
      } else {
        this.form.invoicingFrequencyId = selected;
        this.previousInvoicingFrequencyId = selected;
        // Reset auto-selection flag when user manually changes invoice frequency
        this.isInvoiceFrequencyAutoSelected = false;
      }
    },

    async onAddInvoiceFrequencyOk(invoiceFrequency) {
      this.showAddInvoiceFrequencyModal = false;
      await this.fetchInvoiceFrequencies();
      
      // Auto-select the newly created invoice frequency
      if (invoiceFrequency && invoiceFrequency.id) {
        this.form.invoicingFrequencyId = String(invoiceFrequency.id);
        this.previousInvoicingFrequencyId = String(invoiceFrequency.id);
        // Show success modal
        this.showInvoiceFrequencySuccessModal = true;
      }
    },

    onAddInvoiceFrequencyCancel() {
      this.showAddInvoiceFrequencyModal = false;
      // Reset to previous value when cancelled
      this.form.invoicingFrequencyId = this.previousInvoicingFrequencyId;
    },

    closeInvoiceFrequencySuccessModal() {
      this.showInvoiceFrequencySuccessModal = false;
    },

    handleCustomerChange(eventOrValue) {
      const selected = this.resolveSelectValue(eventOrValue);
      if (selected === '__add_new__') {
        this.showAddCustomerModal = true;
        // Reset the select to previous value after showing modal
        this.$nextTick(() => {
          this.form.customerId = this.previousCustomerId;
        });
      } else {
        this.form.customerId = selected;
        this.previousCustomerId = selected;
        
        // Auto-select payment terms based on selected customer
        this.autoSelectPaymentTermsFromCustomer(selected);
        
        // Auto-select invoice frequency based on selected customer
        this.autoSelectInvoiceFrequencyFromCustomer(selected);
      }
    },

    autoSelectPaymentTermsFromCustomer(customerId) {
      if (!customerId || customerId === '') {
        // If no customer selected, clear payment terms selection
        this.form.paymentTermId = '';
        this.previousPaymentTerm = '';
        this.isPaymentTermsAutoSelected = false;
        return;
      }
      
      // Find the selected customer
      const selectedCustomer = this.customers.find(customer => customer.id == customerId);
      
      logger.log('Auto-selecting payment terms for customer:', {
        customerId,
        selectedCustomer,
        paymentTermId: selectedCustomer?.paymentTermId,
        availablePaymentTerms: this.paymentTerms
      });
      
      if (selectedCustomer && selectedCustomer.paymentTermId) {
        // Verify the payment term exists in our list
        const paymentTerm = this.paymentTerms.find(term => term.id == selectedCustomer.paymentTermId);
        
        if (paymentTerm) {
          // Auto-select the customer's payment terms
          this.form.paymentTermId = selectedCustomer.paymentTermId;
          this.previousPaymentTerm = selectedCustomer.paymentTermId;
          this.isPaymentTermsAutoSelected = true;
          
          // Clear any payment terms validation errors
          delete this.validationErrors.paymentTermId;
          
          logger.log('Payment terms auto-selected:', paymentTerm);
          
          // Show a subtle notification if toast is available
          if (this.$toast) {
            this.$toast.info(`Payment terms automatically set to: ${paymentTerm.name}`);
          }
        } else {
          logger.warn(`Payment term ${selectedCustomer.paymentTermId} not found in payment terms list`);
          // Payment term not found, clear selection
          this.form.paymentTermId = '';
          this.previousPaymentTerm = '';
          this.isPaymentTermsAutoSelected = false;
        }
      } else if (selectedCustomer && !selectedCustomer.paymentTermId) {
        // Customer selected but has no default payment terms
        this.form.paymentTermId = '';
        this.previousPaymentTerm = '';
        this.isPaymentTermsAutoSelected = false;
        
        logger.log('Customer has no default payment terms');
        
        if (this.$toast) {
          this.$toast.info(`Customer "${selectedCustomer.customerName}" has no default payment terms set`);
        }
      }
    },

    autoSelectInvoiceFrequencyFromCustomer(customerId) {
      if (!customerId || customerId === '') {
        // If no customer selected, clear invoice frequency selection
        this.form.invoicingFrequencyId = '';
        this.previousInvoicingFrequencyId = '';
        this.isInvoiceFrequencyAutoSelected = false;
        return;
      }
      
      // Find the selected customer
      const selectedCustomer = this.customers.find(customer => customer.id == customerId);
      
      logger.log('Auto-selecting invoice frequency for customer:', {
        customerId,
        selectedCustomer,
        invoiceFrequencyId: selectedCustomer?.invoiceFrequencyId,
        availableInvoiceFrequencies: this.invoiceFrequencies
      });
      
      if (selectedCustomer && selectedCustomer.invoiceFrequencyId) {
        // Verify the invoice frequency exists in our list
        const invoiceFrequency = this.invoiceFrequencies.find(freq => freq.id == selectedCustomer.invoiceFrequencyId);
        
        if (invoiceFrequency) {
          // Auto-select the customer's invoice frequency
          this.form.invoicingFrequencyId = String(selectedCustomer.invoiceFrequencyId);
          this.previousInvoicingFrequencyId = String(selectedCustomer.invoiceFrequencyId);
          this.isInvoiceFrequencyAutoSelected = true;
          
          logger.log('Invoice frequency auto-selected:', invoiceFrequency);
          
          // Show a subtle notification if toast is available
          if (this.$toast) {
            this.$toast.info(`Invoice frequency automatically set to: ${invoiceFrequency.name}`);
          }
        } else {
          logger.warn(`Invoice frequency ${selectedCustomer.invoiceFrequencyId} not found in invoice frequencies list`);
          // Invoice frequency not found, clear selection
          this.form.invoicingFrequencyId = '';
          this.previousInvoicingFrequencyId = '';
          this.isInvoiceFrequencyAutoSelected = false;
        }
      } else if (selectedCustomer && !selectedCustomer.invoiceFrequencyId) {
        // Customer selected but has no default invoice frequency
        this.form.invoicingFrequencyId = '';
        this.previousInvoicingFrequencyId = '';
        this.isInvoiceFrequencyAutoSelected = false;
        
        logger.log('Customer has no default invoice frequency');
        
        if (this.$toast) {
          this.$toast.info(`Customer "${selectedCustomer.customerName}" has no default invoice frequency set`);
        }
      }
    },

    async onAddCustomerOk(customer) {
      this.showAddCustomerModal = false;
      
      try {
        // Refresh both customers and payment terms to ensure we have the latest data
        await Promise.all([
          this.fetchCustomers(),
          this.fetchPaymentTerms(),
          this.fetchInvoiceFrequencies()
        ]);
        
        // Add a small delay to ensure database has fully updated
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Auto-select the newly created customer
        if (customer && customer.id) {
          this.form.customerId = customer.id;
          this.previousCustomerId = customer.id;
          
          // Wait for next tick to ensure DOM is updated
          await this.$nextTick();
          
          // Auto-select payment terms for the newly created customer if they have default payment terms
          this.autoSelectPaymentTermsFromCustomer(customer.id);
          
          // Auto-select invoice frequency for the newly created customer if they have default invoice frequency
          this.autoSelectInvoiceFrequencyFromCustomer(customer.id);
          
          // Show success message if toast is available
          if (this.$toast) {
            this.$toast.success('Customer created successfully!');
          }
        }
      } catch (error) {
        logger.error('Error handling new customer:', error);
        if (this.$toast) {
          this.$toast.error('Customer created but there was an issue loading the data. Please refresh the page.');
        }
      }
    },

    onAddCustomerCancel() {
      this.showAddCustomerModal = false;
      // Reset to previous value when cancelled
      this.form.customerId = this.previousCustomerId;
    },

    async onAddAccountOk(account) {
      this.showAddAccountModal = false;
      await this.fetchCompanyAccounts();
      
      // Auto-select the newly created account
      if (account && account.id) {
        // Determine which dropdown to update based on account type
        if (account.accountType === 'Income' || account.accountType === 'Other Income') {
          this.form.incomeAccountId = account.id;
          this.previousIncomeAccountId = account.id;
        } else if (account.accountType === 'Expense' || account.accountType === 'Other Expense' || account.accountType === 'Cost of Goods Sold') {
          this.form.expenseAccountId = account.id;
          this.previousExpenseAccountId = account.id;
        } else {
          // For other account types (Bank, Cash, etc.), try to determine based on context
          // If we're in the sell service section, select income account
          if (this.form.sellService && !this.form.buyService) {
            this.form.incomeAccountId = account.id;
            this.previousIncomeAccountId = account.id;
          }
          // If we're in the buy service section, select expense account
          else if (this.form.buyService && !this.form.sellService) {
            this.form.expenseAccountId = account.id;
            this.previousExpenseAccountId = account.id;
          }
          // If both are enabled, default to income account
          else if (this.form.sellService && this.form.buyService) {
            this.form.incomeAccountId = account.id;
            this.previousIncomeAccountId = account.id;
          }
        }
        
        // Show success message
        if (this.$toast) {
          this.$toast.success('Account created and selected successfully!');
        }
      }
    },

    onAddAccountCancel() {
      this.showAddAccountModal = false;
      // Reset to previous values when cancelled
      this.form.incomeAccountId = this.previousIncomeAccountId;
      this.form.expenseAccountId = this.previousExpenseAccountId;
    },

    handlePreferredVendorChange(eventOrValue) {
      const selected = this.resolveSelectValue(eventOrValue);
      if (selected === '__add_new__') {
        this.showAddVendorModal = true;
        // Reset the select to previous value after showing modal
        this.$nextTick(() => {
          this.form.preferredVendorId = this.previousPreferredVendorId;
        });
      } else {
        this.form.preferredVendorId = selected;
        this.previousPreferredVendorId = selected;
        this.clearFieldError('preferredVendorId');
      }
    },

    async onAddVendorOk(vendor) {
      this.showAddVendorModal = false;
      
      try {
        // Refresh vendors list
        await this.fetchVendors();
        
        // Auto-select the newly created vendor
        if (vendor) {
          const preferredVendorId = vendor.id || vendor.vendorId;
          if (preferredVendorId) {
            this.form.preferredVendorId = String(preferredVendorId);
            this.previousPreferredVendorId = String(preferredVendorId);
          }
          
          // Show success message if toast is available
          if (this.$toast) {
            this.$toast.success('Vendor created and selected successfully!');
          }
        }
      } catch (error) {
        logger.error('Error handling new vendor:', error);
        if (this.$toast) {
          this.$toast.error('Vendor created but there was an issue selecting it. Please select it manually.');
        }
      }
    },

    onAddVendorCancel() {
      this.showAddVendorModal = false;
      // Reset to previous value when cancelled
      this.form.preferredVendorId = this.previousPreferredVendorId;
    },
    
    validateFormData() {
      // Additional validation to ensure data types are correct
      const issues = [];
      
      // Check if numeric fields contain valid numbers
      if (this.form.salesPrice && isNaN(Number(this.form.salesPrice))) {
        issues.push('Sales Price must be a valid number');
      }
      if (this.form.purchasePrice && isNaN(Number(this.form.purchasePrice))) {
        issues.push('Purchase Price must be a valid number');
      }
      if (this.form.standardCost && isNaN(Number(this.form.standardCost))) {
        issues.push('Standard Cost must be a valid number');
      }
      
      // Business Logic Validation - Standard Cost vs Sales Price
      if (this.form.salesPrice && this.form.standardCost && !isNaN(Number(this.form.salesPrice)) && !isNaN(Number(this.form.standardCost))) {
        const salesPrice = Number(this.form.salesPrice);
        const standardCost = Number(this.form.standardCost);
        if (standardCost > salesPrice) {
          issues.push(`Standard Cost (${standardCost}) cannot exceed Sales Price (${salesPrice})`);
        }
      }
      
      // Check if ID fields are valid integers
      if (this.form.incomeAccountId && this.form.incomeAccountId !== '' && isNaN(Number(this.form.incomeAccountId))) {
        issues.push('Income Account ID must be a valid number');
      }
      if (this.form.expenseAccountId && this.form.expenseAccountId !== '' && isNaN(Number(this.form.expenseAccountId))) {
        issues.push('Expense Account ID must be a valid number');
      }
      
      if (issues.length > 0) {
        // Show data validation issues modal only when user tries to save
        this.saveError = `Data validation issues:\n${issues.join('\n')}`;
        this.saveSuccess = false;
        this.showSaveModal = true;
        return false;
      }
      
      return true;
    },
    
    closeProfitLossModal() {
      this.showProfitLossModal = false;
    },

    async setPrefilledCustomer() {
      if (!this.prefilledCustomerId) {
        logger.log('No prefilled customer ID provided');
        return;
      }

      logger.log('Setting prefilled customer:', this.prefilledCustomerId);

      // Wait for customers to be loaded
      if (this.customers.length === 0) {
        logger.log('Customers list is empty, fetching...');
        await this.fetchCustomers();
      }

      // Use $nextTick to ensure DOM is ready
      await this.$nextTick();

      // Convert to string for consistency (dropdowns use string values)
      const customerId = String(this.prefilledCustomerId);
      
      // Verify the customer exists in the list
      const customerExists = this.customers.some(c => String(c.id) === customerId);
      if (!customerExists) {
        logger.warn(`Customer with ID ${customerId} not found in customers list. Refetching...`);
        // Try fetching customers again
        await this.fetchCustomers();
        await this.$nextTick();
        
        // Check again after refetch
        const stillNotFound = !this.customers.some(c => String(c.id) === customerId);
        if (stillNotFound) {
          logger.error(`Customer with ID ${customerId} still not found after refetch`);
          return;
        }
      }

      // Set the customer ID - use both string and number comparison
      const matchingCustomer = this.customers.find(c => 
        String(c.id) === customerId || Number(c.id) === Number(customerId)
      );
      
      if (matchingCustomer) {
        // Use the actual customer ID from the list to ensure type consistency
        this.form.customerId = String(matchingCustomer.id);
        this.previousCustomerId = String(matchingCustomer.id);

        logger.log('Customer set in form:', {
          prefilledId: this.prefilledCustomerId,
          formCustomerId: this.form.customerId,
          customerName: matchingCustomer.customerName || matchingCustomer.name
        });

        // Auto-select payment terms and invoice frequency from customer
        this.autoSelectPaymentTermsFromCustomer(String(matchingCustomer.id));
        this.autoSelectInvoiceFrequencyFromCustomer(String(matchingCustomer.id));
      } else {
        logger.error('Could not find matching customer in list');
      }
    },
  },
  watch: {
    async 'visible'(newValue) {
      // When dialog opens, prefill customer if provided
      if (newValue) {
        await Promise.all([
          this.fetchCustomers(),
          this.fetchPaymentTerms(),
          this.fetchInvoiceFrequencies(),
          this.fetchVendors()
        ]);

        // Set prefilled customer after data is loaded
        // Use $nextTick to ensure DOM is ready
        await this.$nextTick();
        if (this.prefilledCustomerId) {
          logger.log('Dialog opened with prefilledCustomerId:', this.prefilledCustomerId);
          await this.setPrefilledCustomer();
        } else {
          logger.log('Dialog opened but no prefilledCustomerId provided');
        }
      }
      if (newValue && this.forcedConsultantType) {
        this.$nextTick(() => {
          const forced = String(this.forcedConsultantType || '');
          if (forced === 'Fulltime-Company') {
            this.form.consultantType = 'Fulltime';
            this.form.isFulltimeCompany = true;
            this.form.sellService = false;
            this.form.buyService = false;
          } else if (forced === 'Fulltime-Client' || forced === 'Fulltime') {
            this.form.consultantType = 'Fulltime';
            this.form.isFulltimeCompany = false;
            this.form.sellService = true;
            this.form.buyService = false;
          } else {
            this.form.consultantType = forced;
            this.form.isFulltimeCompany = false;
          }
          if (forced === 'C2C') {
            this.form.sellService = true;
            this.form.buyService = true;
          }
        });
      }
      if (newValue && this.prefilledVendorId) {
        this.$nextTick(() => {
          if (this.prefilledVendorId) {
            this.form.preferredVendorId = String(this.prefilledVendorId);
            this.previousPreferredVendorId = String(this.prefilledVendorId);
          }
        });
      } else if (!newValue) {
        // Keep success modal alive when closing after save
        if (!this.showSuccessModal) {
          this.form.customerId = '';
          this.previousCustomerId = null;
        }
      }
    },
    
    async prefilledCustomerId(newValue, oldValue) {
      // Watch for changes to prefilledCustomerId prop when dialog is visible
      if (newValue && this.visible && newValue !== oldValue) {
        logger.log('prefilledCustomerId changed:', { newValue, oldValue, visible: this.visible });
        // Ensure customers are loaded first
        if (this.customers.length === 0) {
          await this.fetchCustomers();
        }
        await this.setPrefilledCustomer();
      }
    },
    'form.consultantType'(newValue) {
      // When consultant type is C2C, both services must be enabled
      if (newValue === 'C2C') {
        this.form.sellService = true;
        this.form.buyService = true;
      } else if (!this.isFulltimeCompanyType) {
        // When consultant type is not C2C, disable buy service
        this.form.buyService = false;
      }
    },
    'form.isFulltimeCompany'(isCompany) {
      if (!isCompany) return;
      this.form.sellService = false;
      this.form.buyService = false;
      if (this.activeTab === 'service') {
        this.activeTab = 'basic';
      }
    },
    'form.sellService'() {
      // Re-validate cost vs price relationship when sell service is toggled
      this.validateCostPriceRelationship();
    },
    'form.buyService'() {
      // Re-validate cost vs price relationship when buy service is toggled
      this.validateCostPriceRelationship();
    },
    'form.salesPrice'() {
      // Re-validate cost vs price relationship when sales price changes
      this.validateCostPriceRelationship();
      
      // Always auto-fill standard cost with sales price when sales price changes
      // This ensures standard cost always matches sales price initially
      if (this.form.salesPrice && this.form.sellService) {
        this.form.standardCost = this.form.salesPrice;
        // Format the auto-filled value
        this.formatNumericInput('standardCost');
      }
    },
    'form.purchasePrice'() {
      // Re-validate cost vs price relationship when purchase price changes
      this.validateCostPriceRelationship();
    },
    'form.standardCost'() {
      // Re-validate cost vs price relationship when standard cost changes
      this.validateCostPriceRelationship();
    },
    profitLossInfo: {
      handler(newValue) {
        if (newValue) {
          // Trigger modal after a short delay to ensure values are stable
          setTimeout(() => {
            this.showProfitLossModal = true;
          }, 1000);
        }
      },
      immediate: false
    }
  },
  created() {
    this.fetchCompanyAccounts();
    this.fetchCustomers();
    this.fetchPaymentTerms();
    this.fetchInvoiceFrequencies();
    this.fetchVendors();
  },
};
</script>

<style src="../styles/ConsultantCreationDialog.css"></style> 